import { useState, useEffect, useRef } from "react"
import { useDispatch, useSelector } from "react-redux"
import FullCalendar from "@fullcalendar/react"
import dayGridPlugin from "@fullcalendar/daygrid"
import timeGridPlugin from "@fullcalendar/timegrid"
import interactionPlugin from "@fullcalendar/interaction"
import { Button, Modal, OverlayTrigger, Tooltip } from "react-bootstrap"
import { getBranchesAction } from "../../../store/actions/BranchAction"
import Swal from "sweetalert2"
import PropTypes from "prop-types"
import {
  fetchAvailableTimeSlots,
  bookAppointment,
  cancelLeadAppointment,
  collectAppointmentPayment,
  updateAppointment,
} from "../../../services/AppointmentService"
import { getDoctorsAction } from "../../../store/actions/DoctorsAction"
import {
  rescheduleAppointmentAction,
  rescheduleQueueAppointmentAction,
} from "../../../store/actions/ReceptionistAction"
import { handleSuccess, showErrorAlert } from "../../../services/ErrorHandlingService"
import Select from "react-select"
import "./CommonAppointmentCalendar.css"
import { getSettingUsersAction } from "../../../store/actions/SettingAction"
import { getLeadDetailsAction } from "../../../store/actions/LeadAction"
import { getPatientDetailsAction } from "../../../store/actions/PatientsAction"
import SendPaymentLinkButton from "./SendPaymentLinkButton"
import { fetchAppointmentsLeadAction } from "../../../store/actions/AppointmentActions"
import { getCampaignByDateAction } from "../../../store/actions/CampaignAction";
import Loader from "../Common/Loader";
import ReceptionistAppointmentCalendar from "../Common/ReceptionistAppointmentCalendar";
import TelecallerRescheduleCalendar from "./TelecallerRescheduleCalendar"

const CommonAppointmentCalendar = ({
  module = "telecaller",
  leadId = null,
  patientId = null,
  doctorId = null,
  appointmentType = "",
  currentPatientId = null,
  currentLeadId = null,
  allowBooking = true,
  onBookingSuccess = null,
  onCancelSuccess = null,
  onRescheduleSuccess = null,
  onRescheduleError = null,
  onRescheduleStart = null,
  onRescheduleEnd = null,
  initialStartDate = null,
  initialEndDate = null,
  initialAppointmentId = null,
  currentAppointmentData = null,
  isNewBooking = false,
  onHide = null,
  openFromTelecaller = false,
  openFromServiceCaller = false,
  selectedDate = null,
  refreshTrigger = 0, 

}) => {
  const dispatch = useDispatch();
  const doctors = useSelector((state) => state.doctors.doctors || []);
  const [calendarEvents, setCalendarEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [calendarLoaded, setCalendarLoaded] = useState(false);
  const [selectedDoctorId, setSelectedDoctorId] = useState(doctorId || null);
  const [lastFetchedRange, setLastFetchedRange] = useState(null);
  const [submitted, setSubmitted] = useState(false);

  const firstLoadRef = useRef(true);
  const lastOpenedDateRef = useRef(null);

  // Modal states
  const [consultingFees, setConsultingFees] = useState("")
  const [repeatingConsultingFees, setRepeatingConsultingFees] = useState("")
  const [selectedFeeOption, setSelectedFeeOption] = useState("Consulting Fees")
  const [feeValue, setFeeValue] = useState("")
  const [resolvedFee, setResolvedFee] = useState(0);
  const [isFeeLoading, setIsFeeLoading] = useState(false);

  // Fee states based on mode
  const [onlineConsultingFees, setOnlineConsultingFees] = useState("")
  const [onlineRepeatingConsultingFees, setOnlineRepeatingConsultingFees] = useState("")

  const [showBookingModal, setShowBookingModal] = useState(false)
  const [showRescheduleModal, setShowRescheduleModal] = useState(false)
  const [selectedSlot, setSelectedSlot] = useState(null)
  const [selectedAppointment, setSelectedAppointment] = useState(null)
  const [showPaymentModal, setShowPaymentModal] = useState(false)
  const [isRescheduleMode, setIsRescheduleMode] = useState(false)
  const [rescheduleDate, setRescheduleDate] = useState("")
  const [rescheduleBranch, setRescheduleBranch] = useState(null)
  const [rescheduleMode, setRescheduleMode] = useState("Online")
  const [rescheduleLoading, setRescheduleLoading] = useState(false)
  const [paymentDetails, setPaymentDetails] = useState({
    amount: "",
    paymentType: "",
    coupon: "",
    finalAmount: "",
    appointmentId: null,
  })
  const couponInputRef = useRef()
  const [telecallerNote, setTelecallerNote] = useState("");

  // Store campaign data for specific dates
  const [campaignDataMap, setCampaignDataMap] = useState({});

  const formatDate = (dateString) => {
    const d = new Date(dateString)
    const day = String(d.getDate()).padStart(2, "0")
    const month = String(d.getMonth() + 1).padStart(2, "0")
    const year = d.getFullYear()
    return `${day}-${month}-${year}`
  }

  const userDetails = JSON.parse(localStorage.getItem("userDetails"))
  const roleName = userDetails?.role_name
  const isAdmin = roleName === "Admin"
  const isServiceCaller = roleName === "Service Caller"
  const isTelecaller = roleName === "Telecaller";
  const isReceptionist = roleName?.toLowerCase().trim() === "receptionist";
  const showModal = isTelecaller || isServiceCaller;
  const [refreshLoading, setRefreshLoading] = useState(false);
  // Payment link state
  const [selectedAppointmentForPaymentLink, setSelectedAppointmentForPaymentLink] = useState(null)

  // Lead email state
  const [leadEmail, setLeadEmail] = useState("")
  const [isLeadEmailLoading, setIsLeadEmailLoading] = useState(false)
  const [isEmailFetched, setIsEmailFetched] = useState(false)
  const [patientOrLeadName, setPatientOrLeadName] = useState("")
  
  // Appointment mode - default to Offline
  const [appointmentMode, setAppointmentMode] = useState("Offline")

  const [selectedBranch, setSelectedBranch] = useState(null)
  const branches = useSelector((state) => state.branches?.branches || [])
  const { list = [], num_rows = 0 } = useSelector((state) => state.campaign);

  // Get Redux state for lead and patient details
  const leadDetails = useSelector((state) => state.leadState?.leadDetails)
  const patientDetails = useSelector((state) => state.patients?.patientDetails)
  const [isDoctorDropdownVisible, setIsDoctorDropdownVisible] = useState(true);
  const appointments = useSelector((state) => state.appointmentState?.appointments || []);

  // Helper function to get campaign amount for a specific date
  const getCampaignAmountForDate = (date) => {
    if (!date) return 0;
    
    // First check local campaign data map
    if (campaignDataMap[date]) {
      return campaignDataMap[date];
    }
    
    // Then check Redux state
    if (num_rows > 0 && list && list.length > 0) {
      const campaignForDate = list.find(campaign => {
        const startDate = new Date(campaign.start_date);
        const endDate = new Date(campaign.end_date);
        const checkDate = new Date(date);
        return checkDate >= startDate && checkDate <= endDate;
      });
      
      if (campaignForDate) {
        const amount = Number(campaignForDate.consultation_charges || 0);
        // Store in local map for future reference
        setCampaignDataMap(prev => ({ ...prev, [date]: amount }));
        return amount;
      }
    }
    return 0;
  };

  // Helper function to get campaign title for a specific date
  const getCampaignTitleForDate = (date) => {
    if (!date) return null;
    
    if (num_rows > 0 && list && list.length > 0) {
      const campaignForDate = list.find(campaign => {
        const startDate = new Date(campaign.start_date);
        const endDate = new Date(campaign.end_date);
        const checkDate = new Date(date);
        return checkDate >= startDate && checkDate <= endDate;
      });
      
      return campaignForDate?.title || null;
    }
    return null;
  };

  // Helper function to get amount based on mode, campaign, and appointment_camp_reg
  const getAmountByMode = (mode, isRepeating = false, appointmentDate = null, appointmentCampReg = null) => {
    const upperMode = mode?.toUpperCase() || "OFFLINE";
    
    // If appointment is marked as Campaign, always try to get campaign amount
    if (appointmentCampReg === "Campaign" && appointmentDate) {
      const campaignAmount = getCampaignAmountForDate(appointmentDate);
      if (campaignAmount > 0) {
        return campaignAmount;
      }
    }
    
    // Check for campaign on the specific date (for new bookings)
    if (appointmentDate) {
      const campaignAmount = getCampaignAmountForDate(appointmentDate);
      if (campaignAmount > 0) {
        return campaignAmount;
      }
    }
    
    // If no campaign, use regular fees
    if (upperMode === "ONLINE") {
      return isRepeating 
        ? Number(onlineRepeatingConsultingFees || 0)
        : Number(onlineConsultingFees || 0);
    } else {
      // OFFLINE mode
      return isRepeating
        ? Number(repeatingConsultingFees || 0)
        : Number(consultingFees || 0);
    }
  };

  // Helper function to get amount for display based on role and date
  const getDisplayAmount = (date, mode = null) => {
    const campaignAmount = getCampaignAmountForDate(date);
    if (campaignAmount > 0) {
      return campaignAmount;
    }
    if (mode) {
      return getAmountByMode(mode, false, date);
    }
    return getAmountByMode(appointmentMode, false, date);
  };

  // Fetch campaign data for appointment dates
  useEffect(() => {
    const fetchCampaignForAppointments = async () => {
      if (appointments && appointments.length > 0) {
        const uniqueDates = [...new Set(appointments.map(appt => appt.date))];
        for (const date of uniqueDates) {
          if (!campaignDataMap[date]) {
            const formattedDate = formatDate(date);
            await dispatch(getCampaignByDateAction(formattedDate));
          }
        }
      }
    };
    
    fetchCampaignForAppointments();
  }, [appointments, dispatch]);

  useEffect(() => {
    if (selectedDate) {
      const formattedDate = formatDate(selectedDate);
      dispatch(getCampaignByDateAction(formattedDate));
    }
  }, [selectedDate, dispatch]);

  useEffect(() => {
    console.log("Appointments from Redux:", appointments);
  }, [appointments]);

  useEffect(() => {
    console.log("[v0] Fetching branches for calendar component")
    dispatch(getBranchesAction(false))
  }, [dispatch])
  
  const branchOptions = branches.map((branch) => ({
    value: String(branch.branch_id),
    label: branch.branch_name,
  }))

  // When modal opens, always fetch latest lead details
  useEffect(() => {
    if (showBookingModal && leadId) {
      dispatch(getLeadDetailsAction(leadId));
      setSubmitted(false);
      setLoading(false);
    }
  }, [showBookingModal, leadId, dispatch]);

  useEffect(() => {
    if (!isTelecaller && !isServiceCaller) return;
    if (!showPaymentModal) return;
    if (!selectedAppointmentForPaymentLink?.date) return;

    const formattedDate = formatDate(selectedAppointmentForPaymentLink.date);
    dispatch(getCampaignByDateAction(formattedDate));
  }, [showPaymentModal, selectedAppointmentForPaymentLink, isTelecaller, isServiceCaller, dispatch]);

  useEffect(() => {
    if (!isTelecaller && !isServiceCaller) return;
    if (!showBookingModal) return;
    if (!selectedSlot?.date) return;

    const formattedDate = selectedSlot.date;
    dispatch(getCampaignByDateAction(formattedDate));
  }, [showBookingModal, selectedSlot?.date, isTelecaller, isServiceCaller, dispatch]);

  useEffect(() => {
    const handler = (event) => {
      const { selectedDate } = event.detail;
      if (!selectedDate) return;

      if (showRescheduleModal) {
        console.log("[v0] Event blocked - reschedule modal is active");
        return;
      }

      const isTelecallerEvent = event.type === "telecallerBookClick";
      const isServiceCallerEvent = event.type === "serviceCallerBookClick";

      if (isTelecallerEvent && !openFromTelecaller) return;
      if (isServiceCallerEvent && !openFromServiceCaller) return;

      const d = new Date(selectedDate);
      const formattedDate = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

      const slotData = {
        date: formattedDate,
        time_slot: null,
        doctor_id: doctorId,
        leadId: currentLeadId || leadId,
        patientId: currentPatientId || patientId,
      };

      setSelectedSlot(slotData);
      setShowBookingModal(true);
    };

    window.addEventListener("telecallerBookClick", handler);
    window.addEventListener("serviceCallerBookClick", handler);

    return () => {
      window.removeEventListener("telecallerBookClick", handler);
      window.removeEventListener("serviceCallerBookClick", handler);
    };
  }, [showRescheduleModal, openFromTelecaller, openFromServiceCaller, doctorId, currentLeadId, leadId, currentPatientId, patientId]);

  useEffect(() => {
    const handler = (event) => {
      const { selectedDate, appointment } = event.detail;
      if (!selectedDate || !appointment) return;

      const d = new Date(selectedDate);
      const formattedDate = `${d.getFullYear()}-${String(
        d.getMonth() + 1
      ).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

      setSelectedAppointment(appointment);
      setSelectedSlot({
        date: formattedDate,
        time_slot: null,
        doctor_id: appointment.doctor_id,
      });

      setIsRescheduleMode(true);
      setShowRescheduleModal(true);
    };

    window.addEventListener("telecallerRescheduleClick", handler);
    return () =>
      window.removeEventListener("telecallerRescheduleClick", handler);
  }, []);

  useEffect(() => {
    if (branches.length > 0) {
      let targetBranchId = null;

      if (leadDetails?.branch_id) {
        targetBranchId = String(leadDetails.branch_id);
      } else if (patientDetails?.branch_id) {
        targetBranchId = String(patientDetails.branch_id);
      } else if (!selectedBranch && branches.length > 0) {
        targetBranchId = String(branches[0].branch_id);
      }

      if (targetBranchId) {
        const preselected = branches.find(
          (b) => String(b.branch_id) === targetBranchId
        );
        if (preselected) {
          setSelectedBranch({
            value: String(preselected.branch_id),
            label: preselected.branch_name,
          });
        }
      }
    }
  }, [branches, leadDetails?.branch_id, patientDetails?.branch_id, showBookingModal]);

  useEffect(() => {
    if (refreshTrigger !== undefined && (leadId || patientId)) {
      console.log("🔄 Refresh trigger detected, refetching appointments...");
      if (leadId && patientId) {
        dispatch(fetchAppointmentsLeadAction(leadId, patientId));
      } else if (leadId && !patientId) {
        dispatch(fetchAppointmentsLeadAction(leadId, null));
      } else if (patientId) {
        dispatch(fetchAppointmentsLeadAction(null, patientId));
      }
    }
  }, [refreshTrigger, leadId, patientId, dispatch]);

  useEffect(() => {
    if (leadId && patientId) {
      console.log("✅ Fetching appointments with both leadId & patientId", leadId, patientId);
      dispatch(fetchAppointmentsLeadAction(leadId, patientId));
    } else if (leadId && !patientId) {
      console.log("Only leadId available, fetching basic appointments");
      dispatch(fetchAppointmentsLeadAction(leadId, null));
    } else if (patientId) {
      dispatch(fetchAppointmentsLeadAction(null, patientId));
    }
  }, [leadId, patientId, dispatch]);

  useEffect(() => {
    if (
      showRescheduleModal &&
      selectedAppointment &&
      (isServiceCaller || isTelecaller)
    ) {
      setRescheduleMode(
        selectedAppointment.mode === "ONLINE" ? "Online" : "Offline"
      );
  
      const matchedBranch = branches.find(
        (b) =>
          String(b.branch_id) ===
          String(selectedAppointment.branch_id)
      );
  
      if (matchedBranch) {
        setRescheduleBranch({
          value: String(matchedBranch.branch_id),
          label: matchedBranch.branch_name,
        });
      }
    }
  }, [
    showRescheduleModal,
    selectedAppointment,
    branches,
    isServiceCaller,
    isTelecaller,
  ]);
  
  useEffect(() => {
    if (!isTelecaller && !isServiceCaller) return;
    if (!showPaymentModal) return;

    if (num_rows > 0 && list?.length > 0) {
      setResolvedFee(Number(list[0]?.consultation_charges || 0));
    } else {
      setResolvedFee(Number(consultingFees || 0));
    }
  }, [num_rows, list, consultingFees, isTelecaller, isServiceCaller, showPaymentModal]);

  // Get email and name from Redux store based on appointment type
  const getAppointmentEmail = (appointment) => {
    setIsLeadEmailLoading(true)
    setIsEmailFetched(false)
    setPatientOrLeadName("")

    try {
      const appointmentLeadId = appointment.lead_id || leadId

      if (module === "telecaller") {
        if (leadDetails && leadDetails.lead_id === appointmentLeadId) {
          setLeadEmail(leadDetails.email || "")
          setPatientOrLeadName(
            leadDetails.lead_name || leadDetails.name || leadDetails.first_name + " " + leadDetails.last_name,
          )
          setIsLeadEmailLoading(false)
          setIsEmailFetched(true)
        } else {
          dispatch(getLeadDetailsAction(appointmentLeadId))
            .then((response) => {
              if (response?.data?.data) {
                const leadData = response.data.data
                setLeadEmail(leadData.email || "")
                setPatientOrLeadName(leadData.lead_name || leadData.name || "")
              } else {
                setLeadEmail("")
                setPatientOrLeadName("")
              }
              setIsLeadEmailLoading(false)
              setIsEmailFetched(true)
            })
            .catch((error) => {
              console.error("Error fetching lead email:", error)
              setLeadEmail("")
              setPatientOrLeadName("")
              setIsLeadEmailLoading(false)
              setIsEmailFetched(true)
            })
        }
      } else {
        if (patientDetails && patientDetails.patient_id === appointment.patient_id) {
          setLeadEmail(patientDetails.patient_email || "")
          setPatientOrLeadName(patientDetails.patient_name || patientDetails.name || "")
          setIsLeadEmailLoading(false)
          setIsEmailFetched(true)
        } else {
          dispatch(getPatientDetailsAction(appointment.patient_id))
            .then((response) => {
              if (response?.data?.data) {
                const patientData = response.data.data
                setLeadEmail(patientData.patient_email || "")
                setPatientOrLeadName(patientData.patient_name || patientData.name || "")
              } else {
                setLeadEmail("")
                setPatientOrLeadName("")
              }
              setIsLeadEmailLoading(false)
              setIsEmailFetched(true)
            })
            .catch((error) => {
              console.error("Error fetching patient email:", error)
              setLeadEmail("")
              setPatientOrLeadName("")
              setIsLeadEmailLoading(false)
              setIsEmailFetched(true)
            })
        }
      }
    } catch (error) {
      console.error("Error getting appointment email:", error)
      setLeadEmail("")
      setPatientOrLeadName("")
      setIsLeadEmailLoading(false)
      setIsEmailFetched(true)
    }
  }

  // Module permissions
  const getModulePermissions = () => {
    const basePermissions = {
      telecaller: {
        canBook: true,
        canCancel: true,
        canPayment: false,
        canReschedule: true,
      },
      service_caller: {
        canBook: true,
        canCancel: true,
        canPayment: true,
        canReschedule: true,
      },
      receptionist: {
        canBook: false,
        canCancel: true,
        canPayment: true,
        canReschedule: true,
      },
      default: {
        canBook: true,
        canCancel: false,
        canPayment: false,
        canReschedule: false,
      },
    }

    const modulePermissions = basePermissions[module] || basePermissions.default

    return {
      ...modulePermissions,
      canBook: allowBooking && modulePermissions.canBook,
    }
  }

  const permissions = getModulePermissions()

  // Initialize doctors and fetch initial slots
  useEffect(() => {
    if (doctors.length === 0) {
      dispatch(getDoctorsAction())
    }

    if (initialStartDate && initialEndDate && selectedDoctorId) {
      fetchSlotsForDateRange(initialStartDate, initialEndDate)
    }
  }, [dispatch, doctors.length, selectedDoctorId, initialStartDate, initialEndDate])

  useEffect(() => {
    if (doctorId && doctors.length > 0 && !selectedDoctorId) {
      const foundDoctor = doctors.find((doc) => String(doc.doctor_id) === String(doctorId))
      if (foundDoctor) {
        setSelectedDoctorId(foundDoctor.doctor_id)
      }
    }
  }, [doctorId, doctors, selectedDoctorId])

  // Auto-enter reschedule mode for receptionist when appointment is provided
  useEffect(() => {
    if (module === "receptionist" && (initialAppointmentId || currentAppointmentData)) {
      setIsRescheduleMode(true)
      if (currentAppointmentData) {
        setSelectedAppointment(currentAppointmentData)
      }
    }
  }, [module, initialAppointmentId, currentAppointmentData])

  // Debug effect to log patient ID changes
  useEffect(() => {
    console.log("Patient ID changed:", {
      patientId: patientId,
      currentPatientId: currentPatientId,
      module: module,
    })

    if (patientId || currentPatientId) {
      setCalendarEvents([])
      setLastFetchedRange(null)
      setCalendarLoaded(false)
    }
  }, [patientId, currentPatientId, module])

  useEffect(() => {
    const refreshHandler = () => {
      dispatch(fetchAppointmentsLeadAction(leadId, patientId));
    };
  
    window.addEventListener("appointmentBooked", refreshHandler);
  
    return () =>
      window.removeEventListener("appointmentBooked", refreshHandler);
  }, [leadId, patientId]);
  
  useEffect(() => {
    const fetchFees = async () => {
      try {
        setIsFeeLoading(true);
        const settings = await dispatch(getSettingUsersAction())
  
        if (Array.isArray(settings)) {
          const consulting = settings.find((s) => s.setting_name === "Consulting Fees")
          const repeating = settings.find((s) => s.setting_name === "Repeating Consulting Fees")
          const onlineConsulting = settings.find((s) => s.setting_name === "Online Consulting Fees")
          const onlineRepeating = settings.find((s) => s.setting_name === "Online Repeating Consulting Fees")
  
          if (consulting) setConsultingFees(consulting.setting_value)
          if (repeating) setRepeatingConsultingFees(repeating.setting_value)
          if (onlineConsulting) setOnlineConsultingFees(onlineConsulting.setting_value)
          if (onlineRepeating) setOnlineRepeatingConsultingFees(onlineRepeating.setting_value)
  
          if (roleName === "Service Caller") {
            setFeeValue(consulting?.setting_value || "")
            setSelectedFeeOption("Consulting Fees")
          } else {
            setFeeValue(consulting?.setting_value || "")
            setSelectedFeeOption("Consulting Fees")
          }
        }
      } catch (err) {
        console.error("Failed to fetch fees:", err)
      } finally {
        setIsFeeLoading(false);
      }
    }
  
    fetchFees()
  }, [dispatch, roleName])

  // Update fee when mode or selected fee option changes
  useEffect(() => {
    if (appointmentMode === "Offline") {
      if (selectedFeeOption === "Consulting Fees") {
        setFeeValue(consultingFees);
      } else if (selectedFeeOption === "Repeating Consulting Fees") {
        setFeeValue(repeatingConsultingFees);
      }
    } else if (appointmentMode === "Online") {
      if (selectedFeeOption === "Consulting Fees") {
        setFeeValue(onlineConsultingFees);
      } else if (selectedFeeOption === "Repeating Consulting Fees") {
        setFeeValue(onlineRepeatingConsultingFees);
      }
    }
  }, [appointmentMode, selectedFeeOption, consultingFees, repeatingConsultingFees, onlineConsultingFees, onlineRepeatingConsultingFees]);

  useEffect(() => {
    if (!isServiceCaller && !isTelecaller) {
      if (appointments && appointments.length > 0) {
        const hasWaitingOrCancelled = appointments.some(
          (appt) =>
            appt.appointment_status?.toLowerCase() === "waiting" ||
            appt.appointment_status?.toLowerCase() === "cancelled"
        );
        setIsDoctorDropdownVisible(!hasWaitingOrCancelled);
      } else {
        setIsDoctorDropdownVisible(true);
      }
    } else {
      setIsDoctorDropdownVisible(true);
    }
  }, [appointments, isServiceCaller, isTelecaller]);

  // Helper function to convert time format
  const convertTime = (timeStr) => {
    const [time, period] = timeStr.split(" ")
    const [hours, minutes] = time.split(":")
    let hour = Number.parseInt(hours)

    if (period === "PM" && hour !== 12) {
      hour += 12
    } else if (period === "AM" && hour === 12) {
      hour = 0
    }

    return `${hour.toString().padStart(2, "0")}:${minutes}:00`
  }

  // Helper function to check if a time slot is in the past
  const isTimeSlotInPast = (date, timeSlot) => {
    const now = new Date()
    const [startTime] = timeSlot.split(" to ").map((t) => t.trim())
    const slotDateTime = new Date(`${date}T${convertTime(startTime)}`)
    return slotDateTime < now
  }

  // Function to check if we need to fetch slots for a date range
  const shouldFetchSlots = (start, end) => {
    if (!lastFetchedRange) return true

    const startDate = start.toISOString().split("T")[0]
    const endDate = end.toISOString().split("T")[0]

    const currentPatientIdStr = String(patientId || currentPatientId || "")
    const lastPatientIdStr = lastFetchedRange.patientId || ""
    if (currentPatientIdStr !== lastPatientIdStr) {
      console.log("Patient ID changed, forcing fetch:", {
        current: currentPatientIdStr,
        last: lastPatientIdStr,
      })
      return true
    }

    return (
      startDate !== lastFetchedRange.start ||
      endDate !== lastFetchedRange.end ||
      selectedDoctorId !== lastFetchedRange.doctorId
    )
  }

  // Function to fetch available slots for a date range
  const fetchSlotsForDateRange = async (start, end) => {
    if (!selectedDoctorId) {
      console.log("No doctor selected, skipping fetch")
      return
    }

    if (!shouldFetchSlots(start, end)) {
      console.log("Skipping fetch - already have this range")
      return
    }

    try {
      setLoading(true)
      setCalendarLoaded(false)

      setCalendarEvents([])
      const response = await fetchAvailableTimeSlots({
        leadId: leadId || null,
        patientId: patientId || null,
        doctorId: selectedDoctorId,
        startDate: start.toISOString().split("T")[0],
        endDate: end.toISOString().split("T")[0],
        appointmentType,
      })

      if (response.data?.status === "success" && Array.isArray(response.data.data)) {
        const processedSlots = []
        const currentLeadIdStr = String(leadId || currentLeadId || "")
        const currentPatientIdStr = String(patientId || currentPatientId || "")

        response.data.data.forEach((daySlots) => {
          const date = daySlots.date

          if (daySlots.slots && Array.isArray(daySlots.slots)) {
            daySlots.slots.forEach((slot) => {
              const [startTime, endTime] = slot.time_slot.split(" to ").map((t) => t.trim())

              const startDateTime = new Date(`${date}T${convertTime(startTime)}`)
              const endDateTime = new Date(`${date}T${convertTime(endTime)}`)

              if (slot.is_available === "Yes") {
                const isPastSlot = isTimeSlotInPast(date, slot.time_slot)

                processedSlots.push({
                  id: `available-${date}-${slot.time_slot.replace(/\s/g, "")}-${Math.random()}`,
                  start: startDateTime,
                  end: endDateTime,
                  title: isPastSlot ? "Past" : "",
                  backgroundColor: isPastSlot ? "#f8f9fa" : "transparent",
                  borderColor: isPastSlot ? "#dee2e6" : "#e0e0e0",
                  textColor: isPastSlot ? "#6c757d" : "#000000",
                  classNames: isPastSlot ? ["past-slot", "unavailable-slot"] : ["available-slot"],
                  display: "background",
                  extendedProps: {
                    type: isPastSlot ? "past" : "available",
                    isAvailable: !isPastSlot,
                    isPast: isPastSlot,
                    timeSlot: slot.time_slot,
                    time_slot: slot.time_slot,
                    doctor_id: selectedDoctorId,
                    date: date,
                  },
                })
              } else if (slot.appointmentDetails) {
                const isPastAppointment = isTimeSlotInPast(date, slot.time_slot)

                const appointment = slot.appointmentDetails
                const isCurrentPatient =
                  (currentPatientIdStr && String(appointment.patient_id) === currentPatientIdStr) ||
                  (currentLeadIdStr && String(appointment.lead_id) === currentLeadIdStr)

                let backgroundColor, borderColor, textColor, title

                if (isPastAppointment) {
                  backgroundColor = "#f3f4f6"
                  borderColor = "#d1d5db"
                  textColor = "#6b7280"
                  title = "Past"
                } else if (isCurrentPatient) {
                  if (appointment.is_payment === "Yes") {
                    backgroundColor = "#28a745"
                    borderColor = "#28a745"
                    textColor = "#ffffff"
                    title = "Confirmed"
                  } else {
                    backgroundColor = "#ffc107"
                    borderColor = "#ffc107"
                    textColor = "#000000"
                    title = `Pending`
                  }
                } else {
                  backgroundColor = "#bb2124"
                  borderColor = "#bb2124"
                  textColor = "#ffffff"
                  title = "By Other"
                }

                processedSlots.push({
                  id: `booked-${appointment.appointment_id || Date.now()}`,
                  start: startDateTime,
                  end: endDateTime,
                  title: title,
                  backgroundColor: backgroundColor,
                  borderColor: borderColor,
                  textColor: textColor,
                  classNames: isPastAppointment
                    ? ["past-appointment"]
                    : isCurrentPatient
                      ? ["current-patient-booking"]
                      : ["other-patient-booking"],
                  display: "block",
                  extendedProps: {
                    ...appointment,
                    type: isPastAppointment
                      ? "past_appointment"
                      : isCurrentPatient
                        ? "current_booking"
                        : "other_booking",
                    isBooked: true,
                    isCurrentPatient: isCurrentPatient,
                    isPast: isPastAppointment,
                    time_slot: slot.time_slot,
                    doctor_id: appointment.doctor_id || selectedDoctorId,
                    date: date,
                  },
                })
              }
            })
          }
        })

        setCalendarEvents(processedSlots)
        setLastFetchedRange({
          start: start.toISOString().split("T")[0],
          end: end.toISOString().split("T")[0],
          doctorId: selectedDoctorId,
          patientId: currentPatientIdStr,
        })

        setTimeout(() => {
          setCalendarLoaded(true)
        }, 300)
      } else {
        showErrorAlert("Failed to fetch available time slots")
        setCalendarLoaded(true)
      }
    } catch (error) {
      console.error("Error fetching time slots:", error)
      showErrorAlert("Error fetching available time slots")
      setCalendarLoaded(true)
    } finally {
      setLoading(false)
    }
  }

  // Handle calendar date range change with debouncing
  const handleDatesSet = (arg) => {
    if (!selectedDoctorId) {
      console.log("No doctor selected, skipping date range fetch")
      return
    }

    const startDate = new Date(arg.start)
    const endDate = new Date(arg.end)

    if (arg.view.type === "timeGridWeek") {
      const dayOfWeek = startDate.getDay()
      const diff = dayOfWeek === 0 ? -6 : 1 - dayOfWeek
      startDate.setDate(startDate.getDate() + diff)
      endDate.setTime(startDate.getTime())
      endDate.setDate(startDate.getDate() + 6)
    }

    setTimeout(() => {
      fetchSlotsForDateRange(startDate, endDate)
    }, 100)
  }

  // Handle doctor selection
  const handleDoctorChange = (selectedOption) => {
    setSelectedDoctorId(selectedOption ? selectedOption.value : null)
    setCalendarEvents([])
    setLastFetchedRange(null)
  }

  // Show appointment details for paid (green) slots
  const showPaidAppointmentDetails = (appointment) => {
    let appointmentMode = appointment.mode || "Offline";
    const amount = getAmountByMode(appointment.mode, false, appointment.date, appointment.appointment_camp_reg);
    const campaignTitle = getCampaignTitleForDate(appointment.date);
    
    const currentBranch = branches.find((b) => b.branch_id === appointment.branch_id);
    const branchName = currentBranch?.branch_name || "Unknown Branch";
    let selectedBranch = currentBranch
      ? { value: currentBranch.branch_id, label: currentBranch.branch_name }
      : null;

    const branchSelectContainer = document.createElement("div");
    branchSelectContainer.id = "branchSelectContainer";

    Swal.fire({
      title: '<span style="font-size:2rem;font-weight:700;">Your Appointment Details</span>',
      html: `
        <div class="text-start" style="font-size:1.1rem;">
          <div class="mb-3">
            <p class="mb-1"><strong>Date:</strong> <span style="color:#1a237e;font-weight:600;">${(() => {
          const [year, month, day] = appointment.date.split("-");
          return `${day}-${month}-${year}`;
        })()}</span></p>

            <p class="mb-1"><strong>Status:</strong> <span style="background:#28a745;color:#fff;padding:2px 12px;border-radius:12px;font-size:1rem;font-weight:600;vertical-align:middle;">Waiting</span></p>
            <p class="mb-0"><strong>Payment:</strong> <span style="color:#28a745;font-weight:600;">&#10003; Paid</span></p>
            <p class="mb-1"><strong>Amount:</strong> <span style="color:#28a745;font-weight:600;">₹${Number(amount || 0)}</span>
            ${campaignTitle && appointment.appointment_camp_reg === "Campaign" ? `<small class="text-muted ms-2">(${campaignTitle} Campaign)</small>` : ''}
            </p>
            <p class="mb-1"><strong>Mode:</strong> 
              <span style="background:${appointment.mode === "ONLINE" ? "#17a2b8" : "#6c757d"};color:white;padding:2px 8px;border-radius:8px;font-size:0.9rem;">
                ${appointment.mode || "Not specified"}
              </span>
            </p>
            <p class="mb-1"><strong>Branch:</strong> ${branchName}</p>
          </div>
  
          <div class="mb-3 mt-4" style="border-top: 1px solid #dee2e6; padding-top: 15px;">
            <div class="d-flex align-items-center mb-3">
              <label class="me-3 mb-0"><strong>Appointment Mode:</strong></label>
              <div class="form-check me-3">
                <input type="radio" class="form-check-input" id="offlineMode" name="appointmentMode" value="Offline" />
                <label class="form-check-label" for="offlineMode">Offline</label>
              </div>
              <div class="form-check">
                <input type="radio" class="form-check-input" id="onlineMode" name="appointmentMode" value="Online" />
                <label class="form-check-label" for="onlineMode">Online</label>
              </div>
            </div>
  
            <div class="mb-3">
              <label class="mb-2"><strong>Select Branch:</strong></label>
              <div id="branchSelectContainer"></div>
            </div>
          </div>
        </div>
      `,
      showCancelButton: true,
      showDenyButton: false,
      showConfirmButton: false,
      cancelButtonText: '<span style="font-weight:600;font-size:1.1rem;">Close</span>',
      width: "600px",
      customClass: {
        popup: "appointment-options-popup",
        cancelButton: "btn btn-secondary",
      },
      didOpen: () => {
        const popup = Swal.getPopup();
        popup.style.fontFamily = "inherit";

        const offlineRadio = popup.querySelector("#offlineMode");
        const onlineRadio = popup.querySelector("#onlineMode");
        if (appointment.mode === "OFFLINE") {
          offlineRadio.checked = true;
          appointmentMode = "Offline";
        } else {
          onlineRadio.checked = true;
          appointmentMode = "Online";
        }

        offlineRadio.addEventListener("change", () => {
          if (offlineRadio.checked) appointmentMode = "Offline";
        });
        onlineRadio.addEventListener("change", () => {
          if (onlineRadio.checked) appointmentMode = "Online";
        });

        const branchSelectContainer = popup.querySelector("#branchSelectContainer");
        if (branchSelectContainer && window.React && window.ReactDOM) {
          const SelectComponent = window.React.createElement(Select, {
            options: branchOptions,
            value: selectedBranch,
            onChange: (selectedOption) => {
              selectedBranch = selectedOption;
            },
            placeholder: "Select Branch",
            classNamePrefix: "select",
            isSearchable: true,
            isClearable: false,
            styles: {
              container: (base) => ({ ...base, width: "100%" }),
              control: (base, state) => ({
                ...base,
                minHeight: "55px",
                fontSize: "15px",
                borderRadius: "0.5rem",
                border: state.isFocused ? "1px solid #007bff" : "1px solid #ddd",
                boxShadow: state.isFocused ? "0 0 0 0.2rem rgba(0,123,255,.25)" : "none",
              }),
              menu: (base) => ({ ...base, zIndex: 99999, position: "absolute" }),
            },
          });
          window.ReactDOM.render(SelectComponent, branchSelectContainer);
        } else {
          branchSelectContainer.innerHTML = `
            <select id="branchSelect" class="form-select" style="min-height: 45px; font-size: 15px;">
              ${branchOptions
              .map(
                (option) =>
                  `<option value="${option.value}" ${option.value === selectedBranch?.value ? "selected" : ""
                  }>${option.label}</option>`
              )
              .join("")}
            </select>
          `;
          const branchSelect = branchSelectContainer.querySelector("#branchSelect");
          branchSelect.addEventListener("change", () => {
            selectedBranch = {
              value: branchSelect.value,
              label: branchSelect.options[branchSelect.selectedIndex].text,
            };
          });
        }

        const actions = popup.querySelector(".swal2-actions");

        const updateBtn = document.createElement("button");
        updateBtn.className = "btn btn-primary";
        updateBtn.style.marginRight = "12px";
        updateBtn.innerHTML =
          '<span style="font-weight:600;font-size:1.1rem;">Update Appointment</span>';
        updateBtn.onclick = async () => {
          if (!selectedBranch || !selectedBranch.value) {
            Swal.fire({
              title: "Validation Error",
              text: "Please select a branch before updating the appointment.",
              icon: "warning",
              confirmButtonText: "OK",
            });
            return;
          }

          try {
            Swal.fire({
              title: "Updating Appointment...",
              allowOutsideClick: false,
              didOpen: () => {
                Swal.showLoading();
              },
            });
            const updateData = {
              appointmentId: appointment.appointment_id,
              mode: appointmentMode,
              branchId: selectedBranch.value,
            };
            const response = await updateAppointment(updateData);
            if (response.data?.status === "success") {
              await Swal.fire({
                title: "Success!",
                text: response.data.message || "Appointment updated successfully.",
                icon: "success",
                confirmButtonText: "OK",
              });
              dispatch(fetchAppointmentsLeadAction(leadId, patientId))

              setCalendarEvents((prevEvents) =>
                prevEvents.map((event) =>
                  event.extendedProps?.appointment_id === appointment.appointment_id
                    ? {
                      ...event,
                      extendedProps: {
                        ...event.extendedProps,
                        mode: appointmentMode,
                        branchId: selectedBranch.value,
                      },
                    }
                    : event
                )
              );
            } else {
              throw new Error(response.data?.message || "Update failed");
            }
          } catch (error) {
            Swal.fire({
              title: "Error",
              text: error.message || "Failed to update appointment. Please try again.",
              icon: "error",
              confirmButtonText: "OK",
            });
          }
        };

        actions.insertBefore(updateBtn, actions.firstChild);
      },
    });
  };

  // Update event click handler for green slot
  const handleEventClick = (info) => {
    const eventProps = info.event.extendedProps

    if (!selectedDoctorId) {
      Swal.fire({
        title: "Doctor Not Selected",
        text: "Please select a doctor from the dropdown before booking or rescheduling appointments.",
        icon: "warning",
        confirmButtonText: "OK",
      })
      return
    }

    if (eventProps.isPast) {
      Swal.fire({
        title: "Past Time Slot",
        text: "This time slot is in the past and cannot be booked or modified.",
        icon: "info",
        confirmButtonText: "OK",
      })
      return
    }

    if (eventProps.type === "available") {
      if (module === "receptionist") {
        if (isRescheduleMode) {
          const appointmentToReschedule = selectedAppointment ||
            currentAppointmentData || { appointment_id: initialAppointmentId }

          const isQueueReschedule = appointmentToReschedule.queue_id && !appointmentToReschedule.appointment_id
          const isRegularReschedule = appointmentToReschedule.appointment_id

          if (!appointmentToReschedule || (!isQueueReschedule && !isRegularReschedule)) {
            showErrorAlert("Invalid appointment data. Please try again.")
            return
          }

          const slotData = {
            date: eventProps.date,
            time_slot: eventProps.time_slot,
            doctor_id: eventProps.doctor_id,
          }

          setSelectedSlot(slotData)

          Swal.fire({
            title: "Reschedule Appointment",
            text: `Reschedule appointment to ${eventProps.date} at ${eventProps.time_slot}?`,
            icon: "question",
            showCancelButton: true,
            confirmButtonText: "Yes, Reschedule",
            cancelButtonText: "Cancel",
          }).then((result) => {
            if (result.isConfirmed) {
              if (!selectedAppointment && (currentAppointmentData || initialAppointmentId)) {
                setSelectedAppointment(appointmentToReschedule)
              }

              if (isQueueReschedule) {
                handleQueueReschedule(appointmentToReschedule, slotData)
              } else {
                handleReschedule(appointmentToReschedule, slotData)
              }
            }
          })
          return
        }

        if (isNewBooking) {
          setSelectedSlot({
            date: eventProps.date,
            time_slot: eventProps.time_slot,
            doctor_id: eventProps.doctor_id,
          })
          setShowBookingModal(true)
          return
        }

        if (isRescheduleMode) {
          Swal.fire({
            title: "Reschedule Mode Active",
            text: "Please select an existing appointment to reschedule first.",
            icon: "info",
            confirmButtonText: "OK",
          })
          return
        }
        return
      }

      const slotDateTime = new Date(
        `${eventProps.date}T${eventProps.timeSlot
          ? eventProps.timeSlot.split(" to ")[0]
          : eventProps.time_slot
            ? eventProps.time_slot.split(" to ")[0]
            : "00:00"
        }`,
      )
      const now = new Date()
      if (slotDateTime < now) {
        showErrorAlert("You cannot book an appointment in the past.")
        return
      }

      if (permissions.canBook) {
        setSelectedSlot({
          date: eventProps.date,
          time_slot: eventProps.time_slot,
          doctor_id: eventProps.doctor_id,
        })
        setShowBookingModal(true)
      } else {
        Swal.fire({
          title: "Booking Not Allowed",
          text: "Booking new appointments is not allowed in this context.",
          icon: "warning",
          confirmButtonText: "OK",
        })
      }
    } else if (eventProps.isCurrentPatient && eventProps.is_payment === "No") {
      if (permissions.canPayment || permissions.canCancel) {
        showPaymentCancelOptions(eventProps)
      }
    } else if (eventProps.isCurrentPatient && eventProps.is_payment === "Yes") {
      showPaidAppointmentDetails(eventProps)
    } else if (eventProps.isCurrentPatient && module === "receptionist") {
      setSelectedAppointment(eventProps)
      setIsRescheduleMode(true)
      Swal.fire({
        title: "Appointment Options",
        html: `
          <div style="text-align: left;">
            <p><strong>Time:</strong> ${eventProps.time_slot}</p>
            <p><strong>Status:</strong> ${eventProps.is_payment === "Yes" ? "Confirmed" : "Booked"}</p>
            <p><strong>Doctor:</strong> ${getDoctorName(eventProps.doctor_id)}</p>
          </div>
        `,
        showCancelButton: true,
        showDenyButton: true,
        confirmButtonText: "Reschedule",
        denyButtonText: "Cancel Appointment",
        cancelButtonText: "Close",
      }).then((result) => {
        if (result.isConfirmed) {
          Swal.fire({
            title: "Select New Time Slot",
            text: "Appointment selected for rescheduling. Now click on an available time slot to reschedule.",
            icon: "info",
            confirmButtonText: "OK",
          })
        } else if (result.isDenied) {
          handleCancelAppointment(eventProps.appointment_id)
        } else {
          setIsRescheduleMode(false)
          setSelectedAppointment(null)
        }
      })
    } else if (eventProps.isCurrentPatient) {
      showAppointmentOptions(eventProps)
    } else {
      Swal.fire({
        title: "Appointment Information",
        html: `
          <div style="text-align: left;">
            <p><strong>Time:</strong> ${eventProps.time_slot}</p>
            <p><strong>Status:</strong> Booked by another patient</p>
            <p><strong>Doctor:</strong> ${getDoctorName(eventProps.doctor_id)}</p>
          </div>
        `,
        icon: "info",
        confirmButtonText: "OK",
      })
    }
  }

  // Show appointment options for current patient
  const showAppointmentOptions = (appointment) => {
    const amount = getAmountByMode(appointment.mode, false, appointment.date, appointment.appointment_camp_reg);
    const campaignTitle = getCampaignTitleForDate(appointment.date);
    
    const buttons = []

    if (permissions.canCancel) {
      buttons.push({
        text: "Cancel",
        value: "cancel",
        className: "btn btn-danger",
      })
    }

    if (buttons.length === 0) {
      Swal.fire({
        title: "Appointment Details",
        html: `
          <div style="text-align: left;">
            <div class="mb-3">
              <p class="mb-1"><strong>Date:</strong> <span style="color:#1a237e;font-weight:600;">${(() => {
            const [year, month, day] = appointment.date.split("-")
            return `${day}-${month}-${year}`
          })()}</span></p>
              <p class="mb-1"><strong>Time:</strong> <span style="color:#1a237e;font-weight:600;">${appointment.time_slot}</span></p>
              <p class="mb-1"><strong>Status:</strong> <span style="background:#28a745;color:#fff;padding:2px 12px;border-radius:12px;font-size:1rem;font-weight:600;vertical-align:middle;">Waiting</span></p>
              <p class="mb-1"><strong>Payment:</strong> <span style="color:#28a745;font-weight:600;">&#10003; Paid</span></p>
              <p class="mb-1"><strong>Amount:</strong> <span style="color:#28a745;font-weight:600;">₹${Number(amount || 0)}</span>
              ${campaignTitle && appointment.appointment_camp_reg === "Campaign" ? `<small class="text-muted ms-2">(${campaignTitle} Campaign)</small>` : ''}
              </p>

              ${appointment.branch_name || appointment.branch_id ? `<p class="mb-0"><strong>Branch:</strong> <span style="color:#1a237e;font-weight:600;">${appointment.branch_name || `Branch ${appointment.branch_id}`}</span></p>` : ""}
            </div>
            <p><strong>Doctor:</strong> ${getDoctorName(appointment.doctor_id)}</p>
          </div>
        `,
        icon: "info",
        confirmButtonText: "OK",
      })
      return
    }

    Swal.fire({
      title: "Appointment Options",
      html: `
        <div style="text-align: left;">
          <div class="mb-3">
            <p class="mb-1"><strong>Date:</strong> <span style="color:#1a237e;font-weight:600;">${(() => {
          const [year, month, day] = appointment.date.split("-")
          return `${day}-${month}-${year}`
        })()}</span></p>
            <p class="mb-1"><strong>Time:</strong> <span style="color:#1a237e;font-weight:600;">${appointment.time_slot}</span></p>
            <p class="mb-1"><strong>Status:</strong> <span style="background:#28a745;color:#fff;padding:2px 12px;border-radius:12px;font-size:1rem;font-weight:600;vertical-align:middle;">Waiting</span></p>
            <p class="mb-1"><strong>Payment:</strong> <span style="color:#28a745;font-weight:600;">&#10003; Paid</span></p>
            <p class="mb-1"><strong>Amount:</strong> <span style="color:#28a745;font-weight:600;">₹${Number(amount || 0)}</span>
            ${campaignTitle && appointment.appointment_camp_reg === "Campaign" ? `<small class="text-muted ms-2">(${campaignTitle} Campaign)</small>` : ''}
            </p>

            ${appointment.branch_name || appointment.branch_id ? `<p class="mb-0"><strong>Branch:</strong> <span style="color:#1a237e;font-weight:600;">${appointment.branch_name || `Branch ${appointment.branch_id}`}</span></p>` : ""}
          </div>
          <p><strong>Doctor:</strong> ${getDoctorName(appointment.doctor_id)}</p>
        </div>
      `,
      showCancelButton: true,
      showConfirmButton: false,
      cancelButtonText: "Close",
      showDenyButton: buttons.length > 0,
      denyButtonText: "Actions",
      didOpen: () => {
        const popup = Swal.getPopup()
        const actionsContainer = document.createElement("div")
        actionsContainer.style.marginTop = "15px"
        actionsContainer.style.display = "flex"
        actionsContainer.style.gap = "10px"
        actionsContainer.style.justifyContent = "center"
        actionsContainer.style.flexWrap = "wrap"

        buttons.forEach((button) => {
          const btn = document.createElement("button")
          btn.className = button.className
          btn.textContent = button.text
          btn.style.margin = "5px"
          btn.onclick = () => {
            Swal.close()
            handleAppointmentAction(button.value, appointment)
          }
          actionsContainer.appendChild(btn)
        })

        popup.querySelector(".swal2-actions").appendChild(actionsContainer)
      },
    })
  }

  // Handle appointment actions
  const handleAppointmentAction = (action, appointment) => {
    switch (action) {
      case "cancel":
        handleCancelAppointment(appointment.appointment_id)
        break
      default:
        break
    }
  }

  // Get doctor name
  const getDoctorName = (doctorId) => {
    const doctor = doctors.find((d) => d.doctor_id === doctorId)
    return doctor ? doctor.doctor_name : "Unknown Doctor"
  }

  const handleBookAppointment = async () => {
    if (submitted) return;
    
    if (!selectedBranch || !selectedBranch.value) {
      showErrorAlert("Please select a branch before booking the appointment.");
      return;
    }
  
    setSubmitted(true);
    setLoading(true);

    try {
      let consultationFeesToSend;
      let appointmentCampReg;
      
      const campaignAmount = getCampaignAmountForDate(selectedSlot.date);
      
      if (campaignAmount > 0) {
        consultationFeesToSend = campaignAmount;
        appointmentCampReg = "Campaign";
      } else {
        consultationFeesToSend = appointmentMode === "Offline"
          ? Number(consultingFees || 0)
          : Number(onlineConsultingFees || 0);
        appointmentCampReg = "Regular";
      }

      const payload = {
        leadId,
        patientId,
        date: selectedSlot.date,
        appointmentType,
        mode: appointmentMode,
        branchId: selectedBranch.value,
        appointment_camp_reg: appointmentCampReg,
        consultation_fees: consultationFeesToSend,
        telecaller_note: isTelecaller ? telecallerNote || "" : "",
      };

      const response = await bookAppointment(payload);

      if (response?.data?.status === "success") {
        handleSuccess("Appointment booked successfully!");

        await dispatch(fetchAppointmentsLeadAction(leadId, patientId));

        setShowBookingModal(false);
        setSelectedSlot(null);
        setTelecallerNote("");

        if (isServiceCaller && selectedDoctorId && lastFetchedRange) {
          await fetchSlotsForDateRange(
            new Date(lastFetchedRange.start),
            new Date(lastFetchedRange.end)
          );
        }

        window.dispatchEvent(new Event("appointmentBooked"));
      } else {
        showErrorAlert(response?.data?.message || "Failed to book appointment.");
      }
    } catch (error) {
      console.error("Booking failed:", error);
      showErrorAlert("Something went wrong while booking appointment.");
    } finally {
      setLoading(false);
      setSubmitted(false);
    }
  };

  const showPaymentCancelOptions = (appointment) => {
    const amount = getAmountByMode(appointment.mode, false, appointment.date, appointment.appointment_camp_reg);
    const campaignTitle = getCampaignTitleForDate(appointment.date);

    Swal.fire({
      title: '<span style="font-size:2rem;font-weight:700;">Your Appointment Details</span>',
      html: `
        <div class="text-start" style="font-size:1.1rem;">
          <div class="mb-3">
            <p class="mb-1"><strong>Appointment ID:</strong> ${appointment.appointment_id}</p>
            <p class="mb-1"><strong>Date:</strong> ${(() => {
          const [year, month, day] = appointment.date.split("-")
          return `${day}-${month}-${year}`
        })()}</p>
          
            <p class="mb-1"><strong>Amount:</strong> 
              <span style="color:#28a745;font-weight:600;">₹${amount}</span>
              ${campaignTitle && appointment.appointment_camp_reg === "Campaign" ? `<small class="text-muted ms-2">(${campaignTitle} Campaign)</small>` : ''}
            </p>

            <p class="mb-1"><strong>Status:</strong> <span style="background:#ffc107;color:#222;padding:2px 12px;border-radius:12px;font-size:1rem;font-weight:600;vertical-align:middle;">Waiting</span></p>
            <p class="mb-0"><strong>Payment:</strong> <span style="color:#ff9800;font-weight:600;">Payment Pending</span></p>
          </div>
        </div>  
      `,
      showCancelButton: true,
      showDenyButton: true,
      showConfirmButton: true,
      showCloseButton: true,
      confirmButtonText: '<span style="font-weight:600;font-size:1.1rem;">Make Payment</span>',
      denyButtonText: '<span style="font-weight:600;font-size:1.1rem;">Send Payment Link</span>',
      cancelButtonText: '<span style="font-weight:600;font-size:1.1rem;">Cancel Appointment</span>',
      confirmButtonColor: "#28a745",
      denyButtonColor: "#17a2b8",
      cancelButtonColor: "#6c757d",
      width: "500px",
      customClass: {
        popup: "appointment-options-popup",
        confirmButton: "btn btn-success",
        denyButton: "btn btn-info",
        cancelButton: "btn btn-danger",
      },
      didOpen: () => {
        const popup = Swal.getPopup()
        popup.style.fontFamily = "inherit"
      },
    }).then((result) => {
      if (result.isConfirmed) {
        openCollectPaymentModal(appointment)
      } else if (result.isDenied) {
        const matchedAppt = appointments.find(
          (a) => a.appointment_id === appointment.appointment_id
        );

        const resolvedPatientId =
          appointment.patient_id ||
          appointment?.patient?.patient_id ||
          appointment?.lead?.patient_id ||
          matchedAppt?.patient_id ||
          matchedAppt?.patient?.patient_id ||
          "";

        if (!resolvedPatientId) {
          showErrorAlert("Patient ID not found for this appointment");
          return;
        }

        setSelectedAppointmentForPaymentLink({
          ...appointment,
          patient_id: resolvedPatientId,
        });

        setShowPaymentModal(true);

        getAppointmentEmail({ ...appointment, patient_id: resolvedPatientId });
      } else if (result.dismiss === Swal.DismissReason.cancel) {
        handleCancelAppointment(appointment.appointment_id)
      }
    })
  }

  // Show Collect Payment modal
  const openCollectPaymentModal = (appointment) => {
    setPaymentDetails({
      amount: "",
      paymentType: "",
      coupon: "",
      finalAmount: "",
      appointmentId: appointment.appointment_id,
    })
    setShowPaymentModal(true)
  }

  // Handle Collect Payment form changes
  const handlePaymentInputChange = (e) => {
    const { name, value } = e.target
    setPaymentDetails((prev) => ({ ...prev, [name]: value }))
  }

  const handleApplyCoupon = () => {
    const amt = Number.parseFloat(consultingFees)
    if (!amt || amt <= 0) {
      showErrorAlert("Enter a valid amount before applying the coupon.")
      return
    }

    if (paymentDetails.coupon.trim() === "") {
      showErrorAlert("Please enter a valid coupon code.")
      return
    }

    setPaymentDetails((prev) => ({
      ...prev,
      finalAmount: 0,
    }))

    couponInputRef?.current?.blur()
  }

  const payableAmount = resolvedFee;

  const handleCollectPayment = async () => {
    if (!paymentDetails.paymentType) {
      showErrorAlert("Please select a payment mode");
      return;
    }
    try {
      setLoading(true);
  
      const targetAppointment = appointments.find(
        a => a.appointment_id === paymentDetails.appointmentId
      );
      
      let amountToPay = 0;
      if (targetAppointment) {
        amountToPay = getAmountByMode(targetAppointment.mode, false, targetAppointment.date, targetAppointment.appointment_camp_reg);
      } else {
        amountToPay = appointmentMode === "Offline" 
          ? Number(consultingFees) 
          : Number(onlineConsultingFees);
      }
  
      await collectAppointmentPayment({
        appointment_id: paymentDetails.appointmentId,
        amount: amountToPay,
        payment_type: paymentDetails.paymentType,
        coupon_code: paymentDetails.coupon || null,
        final_amount: paymentDetails.coupon ? 0 : amountToPay,
        lead_id: module === "telecaller" ? (leadId || "0") : "0",
        patient_id:
          module === "service_caller" || module === "receptionist"
            ? (
              patientId ||
              patientDetails?.patient_id ||
              appointments.find(a => a.appointment_id === paymentDetails.appointmentId)?.patient_id ||
              "0"
            )
            : "0",
        appointment_type: appointmentType,
      });
      
      dispatch(fetchAppointmentsLeadAction(leadId, patientId));
  
      setCalendarEvents((prevEvents) =>
        prevEvents.map((event) => {
          if (event.extendedProps?.appointment_id === paymentDetails.appointmentId) {
            return {
              ...event,
              backgroundColor: "#28a745",
              borderColor: "#28a745",
              textColor: "#ffffff",
              title: "Confirmed",
              extendedProps: {
                ...event.extendedProps,
                is_payment: "Yes",
              },
            };
          }
          return event;
        })
      );
      
      if (leadId) {
        dispatch(fetchAppointmentsLeadAction(leadId, patientId));
      }
      
      const updatedEvent = calendarEvents.find(
        (e) => e.extendedProps?.appointment_id === paymentDetails.appointmentId
      );
      if (updatedEvent) {
        const slotDate = updatedEvent.extendedProps.date;
        await fetchSlotsForDateRange(new Date(slotDate), new Date(slotDate));
      }
  
      setShowPaymentModal(false);
      handleSuccess("Payment successful! Appointment confirmed.");
      dispatch(fetchAppointmentsLeadAction(leadId, patientId));
  
    } catch (err) {
      console.error(err);
      showErrorAlert("Failed to process payment. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // Handle cancel appointment
  const handleCancelAppointment = async (appointmentId) => {
    if (!appointmentId) {
      showErrorAlert("No appointment ID provided. Please try again or refresh the page.")
      return
    }
    try {
      const result = await Swal.fire({
        title: "Are you sure?",
        text: "You want to cancel this appointment?",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#d33",
        cancelButtonColor: "#3085d6",
        confirmButtonText: "Yes, cancel it!",
      })

      if (result.isConfirmed) {
        setLoading(true)
        const response = await cancelLeadAppointment(appointmentId)

        if (response.data?.status === "success") {
          handleSuccess("Appointment cancelled successfully!")

          await dispatch(fetchAppointmentsLeadAction(leadId, patientId));
          
          if (lastFetchedRange) {
            await fetchSlotsForDateRange(
              new Date(lastFetchedRange.start),
              new Date(lastFetchedRange.end)
            );
          }
          
          if (onCancelSuccess) {
            onCancelSuccess(appointmentId)
          }
          
          window.dispatchEvent(new Event("appointmentBooked"));
        } else {
          showErrorAlert(response.data?.message || "Failed to cancel appointment")
        }
      }
    } catch (error) {
      console.error("Error cancelling appointment:", error)
      showErrorAlert("Error cancelling appointment")
    } finally {
      setLoading(false)
    }
  }

  const handleSlotSelect = (slot) => {
    setSelectedSlot({
      date: slot.date,
      time_slot: slot.time_slot,
      doctor_id: slot.doctor_id,
    });
  };

  const rescheduleLockRef = useRef(false);

  const handleRescheduleWithApi = async () => {
    if (!rescheduleDate || !rescheduleBranch) {
      showErrorAlert("Please fill all reschedule fields (Date, Branch)");
      return;
    }
  
    const payload = {
      appointment_id: Number(selectedAppointment?.appointment_id),
      date: rescheduleDate,
      mode: rescheduleMode,
      branch: rescheduleBranch?.value || "",
    };
  
    setRescheduleLoading(true);
  
    try {
      const res = await dispatch(rescheduleAppointmentAction(payload));
  
      if (res?.status === "success") {
        handleSuccess("Appointment rescheduled successfully!");
  
        setShowRescheduleModal(false);
        window.isRescheduling = false;
  
        setRescheduleDate("");
        setRescheduleBranch(null);
        setRescheduleMode("Online");
  
        if (patientId) {
          await dispatch(fetchAppointmentsLeadAction(leadId, patientId));
        } else if (leadId) {
          await dispatch(fetchAppointmentsLeadAction(leadId, null));
        }
        
        if (lastFetchedRange) {
          await fetchSlotsForDateRange(
            new Date(lastFetchedRange.start),
            new Date(lastFetchedRange.end)
          );
        }
        
        if (rescheduleDate) {
          dispatch(getCampaignByDateAction(rescheduleDate));
        }
  
        window.dispatchEvent(new Event("appointmentBooked"));
      } else {
        throw new Error(res?.message || "Failed to reschedule");
      }
    } catch (err) {
      showErrorAlert(err.message || "Failed to reschedule appointment");
    } finally {
      setRescheduleLoading(false);
    }
  };

  const handleRescheduleConfirm = () => {
    if (!selectedSlot) {
      alert("Please select a new date/time");
      return;
    }

    setShowRescheduleModal(false);
    window.isRescheduling = false;

    const slotData = {
      date: selectedSlot.date,
      time_slot: selectedSlot.time_slot,
      doctor_id: selectedSlot.doctor_id,
      leadId,
      patientId,
    };

    setSelectedSlot(slotData);
    setShowBookingModal(true);
  };

  const handleReschedule = async (appointmentData = null, slotData = null) => {
    if (rescheduleLockRef.current) {
      console.log("⛔ Reschedule already in progress — ignoring duplicate click");
      return;
    }
    rescheduleLockRef.current = true;

    const appointmentToUse = appointmentData || selectedAppointment;
    const slotToUse = slotData || selectedSlot;

    if (!selectedDoctorId) {
      showErrorAlert("Please select a doctor from the dropdown before rescheduling appointments.");
      rescheduleLockRef.current = false;
      return;
    }

    if (!appointmentToUse || !slotToUse) {
      console.error("Missing data for reschedule:", { appointmentToUse, slotToUse });
      rescheduleLockRef.current = false;
      return;
    }

    try {
      if (onRescheduleStart) onRescheduleStart();
      setLoading(true);

      const reschedulePayload = {
        appointment_id: Number(appointmentToUse?.appointment_id),
        date: slotToUse.date,
        mode: appointmentToUse?.mode || "Offline",
        branch: slotToUse.doctor_id ? String(slotToUse.doctor_id) : "",
      };

      const response = await dispatch(rescheduleAppointmentAction(reschedulePayload));

      if (response && response.status === "success") {
        if (onRescheduleSuccess) {
          onRescheduleSuccess({
            ...response,
            rescheduledAppointment: {
              ...appointmentToUse,
              date: slotToUse.date,
              time_slot: slotToUse.time_slot,
              doctor_id: slotToUse.doctor_id,
            },
            originalAppointment: appointmentToUse,
            newSlot: slotToUse,
          });
        } else {
          handleSuccess("Appointment rescheduled successfully!");
        }

        await dispatch(fetchAppointmentsLeadAction(leadId, patientId));
        
        if (lastFetchedRange) {
          await fetchSlotsForDateRange(
            new Date(lastFetchedRange.start),
            new Date(lastFetchedRange.end)
          );
        }
        
        if (slotToUse.date) {
          dispatch(getCampaignByDateAction(slotToUse.date));
        }

        if (typeof onHide === "function") {
          onHide();
        }

        setShowRescheduleModal(false);
        setSelectedAppointment(null);
        setSelectedSlot(null);
        setIsRescheduleMode(false);
        
        window.dispatchEvent(new Event("appointmentBooked"));
      } else {
        throw new Error(response?.message || "Failed to reschedule");
      }
    } catch (error) {
      console.error("Error rescheduling appointment:", error);
      if (onRescheduleError) onRescheduleError(error);
      else showErrorAlert("Error rescheduling appointment");
    } finally {
      setLoading(false);
      if (onRescheduleEnd) onRescheduleEnd();
      rescheduleLockRef.current = false;
    }
  };

  // Handle reschedule for patient queue (new endpoint)
  const handleQueueReschedule = async (queueData = null, slotData = null) => {
    const queueToUse = queueData || currentAppointmentData
    const slotToUse = slotData || selectedSlot

    if (!selectedDoctorId) {
      showErrorAlert("Please select a doctor from the dropdown before rescheduling appointments.")
      return
    }

    if (!queueToUse || !slotToUse) {
      console.error("Missing data for queue reschedule:", {
        queueToUse,
        slotToUse,
      })
      return
    }

    try {
      if (onRescheduleStart) {
        onRescheduleStart()
      }
      setLoading(true)

      const response = await dispatch(
        rescheduleQueueAppointmentAction({
          queue_id: queueToUse.queue_id,
          patient_id: queueToUse.patient_id,
          reschedule_date: slotToUse.date,
          reschedule_time: slotToUse.time_slot,
        }),
      )

      if (response) {
        if (onRescheduleSuccess) {
          onRescheduleSuccess({
            ...response,
            rescheduledAppointment: {
              ...queueToUse,
              date: slotToUse.date,
              time_slot: slotToUse.time_slot,
              doctor_id: slotToUse.doctor_id,
            },
            originalAppointment: queueToUse,
            newSlot: slotToUse,
          })
        } else {
          handleSuccess("Patient appointment rescheduled successfully!")
        }
        
        await dispatch(fetchAppointmentsLeadAction(leadId, patientId));
        
        if (lastFetchedRange) {
          await fetchSlotsForDateRange(
            new Date(lastFetchedRange.start),
            new Date(lastFetchedRange.end)
          );
        }
        
        if (typeof onHide === "function") {
          onHide();
        }
        
        setShowRescheduleModal(false)
        setSelectedAppointment(null)
        setSelectedSlot(null)
        setIsRescheduleMode(false)
        
        window.dispatchEvent(new Event("appointmentBooked"));
      } else {
        throw new Error(response.data?.message || "Failed to reschedule patient appointment")
      }
    } catch (error) {
      console.error("Error rescheduling patient appointment:", error)

      if (onRescheduleError) {
        onRescheduleError(error)
      } else {
        showErrorAlert(error.message || "Error rescheduling patient appointment")
      }
    } finally {
      setLoading(false)
      if (onRescheduleEnd) {
        onRescheduleEnd()
      }
    }
  }

  // Doctor options for select
  const doctorOptions = doctors.map((doctor) => ({
    value: doctor.doctor_id,
    label: doctor.doctor_name,
    specialization: doctor.specialization,
  }))

  // Format option label for doctor select
  const formatOptionLabel = ({ label, specialization }) => (
    <div>
      <div style={{ fontWeight: "bold" }}>{label}</div>
      <div style={{ fontSize: "0.8em", color: "#666" }}>{specialization}</div>
    </div>
  )

  const handleBranchChange = (selectedOption) => {
    setSelectedBranch(selectedOption)
  }

  // Render event content
  const renderEventContent = (eventInfo) => {
    const event = eventInfo.event
    const eventDetails = eventInfo.event.extendedProps
    const eventType = eventDetails.type

    if (eventDetails.isPast) {
      return (
        <div className="past-slot-content">
          <div
            style={{
              fontSize: "11px",
              color: "#6c757d",
              textAlign: "center",
              padding: "4px",
              fontWeight: "500",
              opacity: "0.7",
            }}
          >
            Past
          </div>
        </div>
      )
    }

    if (eventType === "available") {
      const slotDateTime = new Date(
        `${eventDetails.date}T${eventDetails.timeSlot
          ? eventDetails.timeSlot.split(" to ")[0]
          : eventDetails.time_slot
            ? eventDetails.time_slot.split(" to ")[0]
            : "00:00"
        }`,
      )
      const now = new Date()
      const isPast = slotDateTime < now
      return (
        <div
          className="available-slot-indicator"
          style={isPast ? { opacity: 0.4, pointerEvents: "none", filter: "grayscale(1)" } : {}}
        ></div>
      )
    }

    if (eventType === "other_booking") {
      return (
        <div className="other-booking-content">
          <div
            style={{
              fontSize: "11px",
              color: "white",
              textAlign: "center",
              padding: "4px",
              fontWeight: "500",
              opacity: "0.8",
            }}
          >
            Booked By Other
          </div>
        </div>
      )
    }

    return (
      <div className="current-booking-content">
        <div
          style={{
            fontSize: "12px",
            textAlign: "center",
            padding: "4px",
            fontWeight: "600",
          }}
        >
          {event.title}
        </div>
      </div>
    )
  }

  const filteredAppointments = (() => {
    let list = appointments.filter(
      (appt) =>
        String(appt.lead_id) === String(leadId) ||
        String(appt.patient_id) === String(patientId)
    );

    if (isTelecaller && list.length > 0) {
      list = [...list]
        .sort((a, b) => new Date(b.date) - new Date(a.date))
        .slice(0, 1);
    }

    return list;
  })();

  const selectedDoctorOption = doctorOptions.find((option) => String(option.value) === String(selectedDoctorId))

  return (
    <>
      <div className={`calendar-minimal ${isAdmin|| isTelecaller || isServiceCaller ? "d-none" : ""}`}>

        {!isAdmin &&!isTelecaller && !isServiceCaller && !appointments.some(
          (appt) =>
            String(appt.lead_id) === String(leadId) ||
            String(appt.patient_id) === String(patientId)
        ) && (
            <div
              className="calendar-legend mb-3"
              style={{
                display: "flex",
                gap: "12px",
                alignItems: "center",
                flexWrap: "wrap",
              }}
            >
              <span
                className="legend-item"
                style={{
                  background: "#ffc107",
                  color: "#222",
                  borderRadius: "8px",
                  padding: "2px 12px",
                  fontWeight: 400,
                  fontSize: "12px",
                }}
              >
                Pending
              </span>
              <span
                className="legend-item"
                style={{
                  background: "#28a745",
                  color: "#fff",
                  borderRadius: "8px",
                  padding: "2px 12px",
                  fontWeight: 400,
                  fontSize: "12px",
                }}
              >
                Confirmed
              </span>
              <span
                className="legend-item"
                style={{
                  background: "#bb2124",
                  color: "#fff",
                  borderRadius: "8px",
                  padding: "2px 12px",
                  fontWeight: 400,
                  fontSize: "12px",
                }}
              >
                Booked By Other
              </span>
              <span
                className="legend-item"
                style={{
                  background: "rgba(23,162,184,0.1)",
                  color: "#17a2b8",
                  border: "1px dashed #17a2b8",
                  borderRadius: "8px",
                  padding: "2px 12px",
                  fontWeight: 400,
                  fontSize: "12px",
                }}
              >
                Available
              </span>
            </div>
          )}

        {!isAdmin &&!isTelecaller && !isServiceCaller && isDoctorDropdownVisible && (
          <Select
            value={selectedDoctorOption || null}
            onChange={handleDoctorChange}
            options={doctorOptions}
            formatOptionLabel={formatOptionLabel}
            placeholder="Select a doctor..."
            isClearable
            className="mb-3"
            classNamePrefix="react-select"
            isDisabled={loading}
            menuPortalTarget={document.body}
            styles={{
              menuPortal: (base) => ({ ...base, zIndex: 2000 }),
              menu: (base) => ({ ...base, zIndex: 2000 }),
            }}
          />
        )}

        {!isAdmin && !isTelecaller && !isServiceCaller && !selectedDoctorId && (
          <div className="alert alert-warning mb-3" role="alert">
            <i className="fas fa-exclamation-triangle me-2"></i>
            <strong>Doctor Selection Required:</strong> Please select a doctor from the dropdown above before booking
            appointments.
          </div>
        )}

        {calendarEvents.length === 0 && !loading && calendarLoaded && selectedDoctorId && (
          <div className="text-center mt-3">
            <Button
              variant="outline-primary"
              onClick={() => {
                if (selectedDoctorId) {
                  const startDate = new Date(initialStartDate || new Date())
                  const endDate = new Date(initialEndDate || new Date())
                  if (initialStartDate && initialEndDate) {
                    if (initialStartDate.getDay() === 0) {
                      startDate.setDate(startDate.getDate() - 6)
                    } else {
                      startDate.setDate(startDate.getDate() - startDate.getDay() + 1)
                    }
                    endDate.setDate(startDate.getDate() + 6)
                  }
                  fetchSlotsForDateRange(startDate, endDate)
                } else {
                  showErrorAlert("Please select a doctor first.")
                }
              }}
              disabled={loading}
            >
              Retry Fetch Slots
            </Button>
          </div>
        )}
      </div>

      {/* Appointment Details Section - FIXED: Now properly handling campaign amounts */}
      {((isAdmin || isTelecaller || isServiceCaller) && filteredAppointments.length > 0) ? (
        // <div className="alert alert-info mt-3 p-3 shadow-sm rounded">
        //   <h5 className="fw-bold mb-3 text-center">Appointment Details</h5>
        //   {filteredAppointments.map((appt, idx) => {
        //     const currentBranch = branches.find((b) => b.branch_id === appt.branch_id);
        //     const branchName = currentBranch?.branch_name || "Unknown Branch";
        //     // Pass appointment_camp_reg to getAmountByMode to properly get campaign amount
        //     const amount = getAmountByMode(appt.mode, false, appt.date, appt.appointment_camp_reg);
        //     const campaignTitle = getCampaignTitleForDate(appt.date);
        //     // Check if this appointment is from campaign
        //     const isCampaignAppointment = appt.appointment_camp_reg === "Campaign";

        //     return (
        //       <div key={idx} className="mb-3 border-bottom pb-2">
        //         <p className="mb-1">
        //           <strong>Date:</strong>{" "}
        //           <span style={{ color: "#1a237e", fontWeight: "600" }}>
        //             {(() => {
        //               const [year, month, day] = appt.date.split("-");
        //               return `${day}-${month}-${year}`;
        //             })()}
        //           </span>
        //         </p>
                
        //         <p className="mb-1">
        //           <strong>Status: </strong>{" "}
        //           <span
        //             style={{
        //               background:
        //                 appt.appointment_status?.toLowerCase() === "waiting"
        //                   ? "#28a745"
        //                   : appt.appointment_status?.toLowerCase() === "cancel" ||
        //                     appt.appointment_status?.toLowerCase() === "cancelled"
        //                     ? "#dc3545"
        //                     : "#6c757d",
        //               color: "#fff",
        //               padding: "2px 12px",
        //               borderRadius: "12px",
        //               fontSize: "0.9rem",
        //               fontWeight: "600",
        //               textTransform: "capitalize",
        //             }}
        //           >
        //             {appt.appointment_status || "N/A"}
        //           </span>
        //         </p>

        //         <p className="mb-1">
        //           <strong>Amount:</strong>{" "}
        //           <span style={{ color: "#28a745", fontWeight: "600" }}>
        //             ₹{Number(amount || 0)}
        //             {campaignTitle && isCampaignAppointment && (
        //               <small className="text-muted ms-2">({campaignTitle} Campaign)</small>
        //             )}
        //           </span>
        //         </p>

        //         <p className="mb-0 d-flex align-items-center gap-2">
        //           <strong>Payment:</strong>

        //           {appt.is_payment === "Yes" ? (
        //             <span style={{ color: "#28a745", fontWeight: "600" }}>✓ Paid</span>
        //           ) : (
        //             <>
        //               <span style={{ color: "brown", fontWeight: "600" }}>✘ Not Paid</span>

        //               {["cancel", "cancelled"].includes(appt.appointment_status?.toLowerCase()) ? (
        //                 <Button
        //                   size="sm"
        //                   variant="outline-primary"
        //                   onClick={() => {
        //                     setSelectedAppointment(appt);
        //                     setIsRescheduleMode(true);
        //                     setShowRescheduleModal(true);
        //                     setSelectedSlot(null);
        //                     window.isRescheduling = true;
        //                     setRescheduleDate("");
        //                     setRescheduleBranch(null);
        //                     setRescheduleMode("Online");
        //                   }}
        //                 >
        //                   Reschedule
        //                 </Button>
        //               ) : (
        //                 <Button
        //                   size="sm"
        //                   variant="outline-success"
        //                   onClick={() => showPaymentCancelOptions(appt)}
        //                 >
        //                   Options
        //                 </Button>
        //               )}
        //             </>
        //           )}
        //         </p>

        //         {appt.mode && (
        //           <p className="mb-1">
        //             <strong>Mode:</strong>{" "}
        //             <span
        //               style={{
        //                 background: appt.mode === "ONLINE" ? "#17a2b8" : "#6c757d",
        //                 color: "#fff",
        //                 padding: "2px 8px",
        //                 borderRadius: "6px",
        //                 fontSize: "0.8rem",
        //                 fontWeight: "600",
        //                 textTransform: "uppercase",
        //               }}
        //             >
        //               {appt.mode}
        //             </span>
        //           </p>
        //         )}
                
        //         <p className="mb-1">
        //           <strong>Branch:</strong>{" "}
        //           <span style={{ color: "#1a237e", fontWeight: "600" }}>
        //             {branchName}
        //           </span>
        //         </p>
                
        //         {isCampaignAppointment && campaignTitle && (
        //           <p className="mb-0">
        //             <small className="text-primary">
        //               <i className="fas fa-tag me-1"></i>
        //               Campaign: {campaignTitle}
        //             </small>
        //           </p>
        //         )}
        //       </div>
        //     );
        //   })}
        // </div>
        <div className="timeline-panel flex-wrap border-0 p-3 rounded mt-3">
   <div className="d-flex justify-content-between align-items-center mb-3">
    
    <h5 className="fw-bold text-center m-0">
      Appointment Details
    </h5>

    {(leadId || patientId) && (
  <>
    {refreshLoading ? (
      <span
        className="spinner-border spinner-border-sm text-info"
        style={{ width: "18px", height: "18px" }}
      />
    ) : (
      <i
        className="fa fa-refresh"
        style={{
          cursor: "pointer",
          fontSize: "18px",
          color: "#0dcaf0",
        }}
        title="Refresh Appointment Status"
        onClick={async () => {
          setRefreshLoading(true);

          await dispatch(fetchAppointmentsLeadAction(leadId, patientId));

          setRefreshLoading(false);
        }}
      ></i>
    )}
  </>
)}

  </div>
 
  <div className="row">
    {filteredAppointments.map((appt, idx) => {
      const currentBranch = branches.find(
        (b) => b.branch_id === appt.branch_id
      );
      const branchName = currentBranch?.branch_name || "Unknown Branch";

      const amount = getAmountByMode(
        appt.mode,
        false,
        appt.date,
        appt.appointment_camp_reg
      );

      const campaignTitle = getCampaignTitleForDate(appt.date);
      const isCampaignAppointment =
        appt.appointment_camp_reg === "Campaign";

      return (
        <div key={idx} className="col-md-4 mb-3">
          {/* ✅ Same design wrapped in box */}
          <div className=" custom-timeline-panel timeline-panel bgl-dark border rounded p-2 h-100">

          <p className="mb-1 d-flex justify-content-between align-items-center">

{/* Left: Date */}
<span>
  <strong>Date:</strong>{" "}
  <span style={{ color: "#1a237e", fontWeight: "600" }}>
    {(() => {
      const [year, month, day] = appt.date.split("-");
      return `${day}-${month}-${year}`;
    })()}
  </span>
</span>



</p>

            <p className="mb-1">
              <strong>Status: </strong>{" "}
              <span
                style={{
                  background:
                    appt.appointment_status?.toLowerCase() === "waiting"
                      ? "#28a745"
                      : appt.appointment_status?.toLowerCase() === "cancel" ||
                        appt.appointment_status?.toLowerCase() === "cancelled"
                      ? "#dc3545"
                      : "#6c757d",
                  color: "#fff",
                  padding: "2px 12px",
                  borderRadius: "12px",
                  fontSize: "0.9rem",
                  fontWeight: "600",
                  textTransform: "capitalize",
                }}
              >
                {appt.appointment_status || "N/A"}
              </span>
            </p>

            <p className="mb-1">
              <strong>Amount:</strong>{" "}
              <span style={{ color: "#28a745", fontWeight: "600" }}>
                ₹{Number(amount || 0)}
                {campaignTitle && isCampaignAppointment && (
                  <small className="text-muted ms-2">
                    ({campaignTitle} Campaign)
                  </small>
                )}
              </span>
            </p>

            <p className="mb-0 d-flex align-items-center gap-2">
              <strong>Payment:</strong>

              {appt.is_payment === "Yes" ? (
                <span style={{ color: "#28a745", fontWeight: "600" }}>
                  ✓ Paid
                </span>
              ) : (
                <>
                  <span style={{ color: "brown", fontWeight: "600" }}>
                    ✘ Not Paid
                  </span>

                  {["cancel", "cancelled"].includes(
                    appt.appointment_status?.toLowerCase()
                  ) ? (
                    <Button
                      size="sm"
                      variant="outline-primary"
                      onClick={() => {
                        setSelectedAppointment(appt);
                        setIsRescheduleMode(true);
                        setShowRescheduleModal(true);
                        setSelectedSlot(null);
                        window.isRescheduling = true;
                        setRescheduleDate("");
                        setRescheduleBranch(null);
                        setRescheduleMode("Online");
                      }}
                    >
                      Reschedule
                    </Button>
                  ) : (
                    <Button
                      size="sm"
                      variant="outline-success"
                      onClick={() => showPaymentCancelOptions(appt)}
                    >
                      Options
                    </Button>
                  )}
                </>
              )}
            </p>

            {appt.mode && (
              <p className="mb-1">
                <strong>Mode:</strong>{" "}
                <span
                  style={{
                    background:
                      appt.mode === "ONLINE" ? "#17a2b8" : "#6c757d",
                    color: "#fff",
                    padding: "2px 8px",
                    borderRadius: "6px",
                    fontSize: "0.8rem",
                    fontWeight: "600",
                    textTransform: "uppercase",
                  }}
                >
                  {appt.mode}
                </span>
              </p>
            )}

            <p className="mb-1">
              <strong>Branch:</strong>{" "}
              <span style={{ color: "#1a237e", fontWeight: "600" }}>
                {branchName}
              </span>
            </p>

            {isCampaignAppointment && campaignTitle && (
              <p className="mb-0">
                <small className="text-primary">
                  <i className="fas fa-tag me-1"></i>
                  Campaign: {campaignTitle}
                </small>
              </p>
            )}
          </div>
        </div>
      );
    })}
  </div>
</div>
      ) : null}

      {isAdmin||isTelecaller || isServiceCaller ? (
        <></>
      ) : selectedDoctorId ? (
        <div className="calendar-wrapper position-relative">
          {(loading || !calendarLoaded) && (
            <div className="calendar-loading-overlay">
              <div className="d-flex flex-column align-items-center">
                <div className="spinner-border text-primary mb-2" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
                <small className="text-muted fw-medium">
                  {loading ? "Loading appointments..." : "Rendering calendar..."}
                </small>
              </div>
            </div>
          )}

          <FullCalendar
            plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
            initialView="timeGridWeek"
            headerToolbar={{
              left: "prev,next today",
              center: "title",
              right: "timeGridWeek",
            }}
            events={calendarEvents}
            editable={false}
            selectable={false}
            eventClick={handleEventClick}
            datesSet={handleDatesSet}
            eventContent={renderEventContent}
            height="auto"
            slotMinTime="09:00:00"
            slotMaxTime="20:00:00"
            allDaySlot={false}
            eventDisplay="block"
            nowIndicator={true}
            scrollTime="09:00:00"
            slotDuration="00:30:00"
            firstDay={1}
            expandRows={true}
            handleWindowResize={false}
            loading={loading}
          />
        </div>
      ) : (
        <div className="text-center mt-4">
          <div className="alert alert-info" role="alert">
            <i className="fas fa-calendar-alt me-2"></i>
            <strong>Calendar View:</strong> Please select a doctor from the dropdown above to view available appointment slots.
          </div>
        </div>
      )}

      {/* Booking Modal */}
      <Modal
        show={showBookingModal}
        onHide={() => {
          setShowBookingModal(false);
          setSubmitted(false);
        }}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Book Appointment</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          {selectedSlot && (
            <>
              <p className="mt-0">
                <strong>Date:</strong> {formatDate(selectedSlot.date)}
              </p>

              <div className="mb-2 mt-1">
                <div className="d-flex align-items-center mb-3">
                  <label className="me-3">
                    <strong>Appointment Mode:</strong>
                  </label>
                  <div className="form-check me-3">
                    <input
                      type="radio"
                      className="form-check-input"
                      id="offlineMode"
                      name="appointmentMode"
                      value="Offline"
                      checked={appointmentMode === "Offline"}
                      onChange={() => {
                        setAppointmentMode("Offline");
                      }}
                    />
                    <label className="form-check-label" htmlFor="offlineMode">
                      Offline
                    </label>
                  </div>
                  <div className="form-check">
                    <input
                      type="radio"
                      className="form-check-input"
                      id="onlineMode"
                      name="appointmentMode"
                      value="Online"
                      checked={appointmentMode === "Online"}
                      onChange={() => {
                        setAppointmentMode("Online");
                      }}
                    />
                    <label className="form-check-label" htmlFor="onlineMode">
                      Online
                    </label>
                  </div>
                </div>

                {(() => {
                  const campaignAmount = getCampaignAmountForDate(selectedSlot.date);
                  const campaignTitle = getCampaignTitleForDate(selectedSlot.date);
                  
                  if (campaignAmount > 0) {
                    return (
                      <div className="mb-3">
                        <label>
                          <strong>Campaign Amount</strong>
                        </label>
                        <div className="mt-1">
                          <div className="text-muted small mb-1">{campaignTitle}</div>
                          <p className="mb-1 fw-bold text-success">
                            ₹{campaignAmount}
                          </p>
                        </div>
                      </div>
                    );
                  } else {
                    return (
                      <div className="mb-2 mt-0">
                        <label className="mb-1 d-block">
                          <strong>Amount</strong>
                        </label>
                        <p className="mb-1">
                          ₹{Number(appointmentMode === "Offline" ? consultingFees || 0 : onlineConsultingFees || 0)}
                        </p>
                      </div>
                    );
                  }
                })()}

                <div style={{ minWidth: "200px", marginTop: "0" }}>
                  <Select
                    options={branchOptions}
                    value={selectedBranch}
                    onChange={handleBranchChange}
                    placeholder="Select Branch"
                    classNamePrefix="select"
                    isSearchable={false}
                    isClearable={false}
                    styles={{
                      container: (base) => ({
                        ...base,
                        width: "100%",
                      }),
                      control: (base, state) => ({
                        ...base,
                        minHeight: "55px",
                        fontSize: "15px",
                        borderRadius: "0.5rem",
                        border: state.isFocused
                          ? "1px solid #007bff"
                          : "1px solid #ddd",
                        boxShadow: state.isFocused
                          ? "0 0 0 0.2rem rgba(0,123,255,.25)"
                          : "none",
                      }),
                      menu: (base) => ({
                        ...base,
                        zIndex: 99999,
                        position: "absolute",
                      }),
                    }}
                  />
                </div>
              </div>

              {isTelecaller && (
                <div className="mb-2 mt-0">
                  <label className="form-label">
                    <strong>Telecaller Note (optional)</strong>
                  </label>
                  <textarea
                    className="form-control"
                    placeholder="Enter note..."
                    value={telecallerNote}
                    onChange={(e) => setTelecallerNote(e.target.value)}
                    style={{ minHeight: "50px" }}
                  />
                </div>
              )}
            </>
          )}
        </Modal.Body>

        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowBookingModal(false)}>
            Cancel
          </Button>

          <Button
            variant="primary"
            onClick={handleBookAppointment}
            disabled={submitted || loading}
          >
            {loading ? (
              <>
                <Loader size="sm" />
                <span className="ms-2">Booking...</span>
              </>
            ) : (
              "Book Appointment"
            )}
          </Button>
        </Modal.Footer>
      </Modal>
      
      {/* Telecaller/Receptionist Reschedule Modal */}
      {isReceptionist && (
        <Modal
          show={showRescheduleModal}
          onHide={() => setShowRescheduleModal(false)}
          size="xl"
          centered
        >
          <Modal.Header closeButton>
            <Modal.Title>Reschedule Appointment</Modal.Title>
          </Modal.Header>

          <Modal.Body>
            {selectedAppointment && (
              <div className="mb-3">
                <h6>Current Appointment:</h6>
                <p><strong>Date:</strong> {formatDate(selectedAppointment?.date)}</p>
               
                <p><strong>Doctor:</strong> {getDoctorName(selectedAppointment.doctor_id)}</p>
                <p><strong>Mode:</strong> {selectedAppointment.mode}</p>
                <p><strong>Amount:</strong> ₹{getAmountByMode(selectedAppointment.mode, false, selectedAppointment.date, selectedAppointment.appointment_camp_reg)}</p>
              </div>
            )}

            <div className="border rounded p-2" style={{ background: "#fafafa" }}>
              {isTelecaller ? (
                <TelecallerRescheduleCalendar appointment={selectedAppointment} />
              ) : (
                <ReceptionistAppointmentCalendar
                  patientId={patientId}
                  doctorId={doctorId}
                  initialAppointmentId={selectedAppointment?.appointment_id}
                  currentAppointmentData={selectedAppointment}
                  onSlotSelect={handleSlotSelect}
                />
              )}
            </div>

            {selectedSlot && (
              <div>
                <h6>New Time Slot:</h6>
                <p><strong>Date:</strong> {formatDate(selectedSlot.date)}</p>
                <p><strong>Time:</strong> {selectedSlot.time_slot}</p>
                <p><strong>Doctor:</strong> {getDoctorName(selectedSlot.doctor_id)}</p>
              </div>
            )}

            {!selectedSlot && (
              <p className="text-muted">
                Please select a new time slot from the calendar.
              </p>
            )}
          </Modal.Body>

          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowRescheduleModal(false)}>
              Cancel
            </Button>
            <Button
              variant="primary"
              disabled={!selectedSlot}
              onClick={handleRescheduleConfirm}
            >
              Confirm
            </Button>
          </Modal.Footer>
        </Modal>
      )}
      
      {/* Reschedule Modal for Telecaller/Service Caller */}
      {showModal && (
        <Modal
          show={showRescheduleModal}
          size="lg"
          centered
          onHide={() => {
            setShowRescheduleModal(false);
            window.isRescheduling = false;
          }}
        >
          <Modal.Header closeButton>
            <Modal.Title className="fw-semibold">
              Reschedule Appointment
            </Modal.Title>
          </Modal.Header>

          <Modal.Body>
            {selectedAppointment && (
              <div className="mb-3 p-2 bg-light rounded">
                <h6 className="mb-2">Current Appointment:</h6>
                <p className="mb-1"><strong>Date:</strong> {formatDate(selectedAppointment.date)}</p>
               
                <p className="mb-1"><strong>Mode:</strong> {selectedAppointment.mode}</p>
                <p className="mb-0"><strong>Amount:</strong> ₹{getAmountByMode(selectedAppointment.mode, false, selectedAppointment.date, selectedAppointment.appointment_camp_reg)}</p>
              </div>
            )}

            <div className="mb-3">
              <label className="form-label fw-semibold">New Date</label>
              <input
                type="date"
                className="form-control"
                value={rescheduleDate}
                min={new Date().toISOString().split("T")[0]}
                onChange={(e) => setRescheduleDate(e.target.value)}
              />
            </div>

            <div className="mb-3">
              <label className="form-label fw-semibold text-dark mb-2">
                Branch
              </label>

              <Select
                value={rescheduleBranch}
                onChange={(selectedOption) => setRescheduleBranch(selectedOption)}
                options={branches.map((branch) => ({
                  value: branch.branch_id || branch.value,
                  label: branch.branch_name || branch.label,
                }))}
                placeholder="Select Branch"
                menuPortalTarget={document.body}
                menuPosition="fixed"
                classNamePrefix="select"
                styles={{
                  control: (base, state) => ({
                    ...base,
                    minHeight: "45px",
                    fontSize: "14px",
                    borderRadius: "6px",
                    border: state.isFocused
                      ? "1px solid #0d6efd"
                      : "1px solid #ced4da",
                    boxShadow: state.isFocused
                      ? "0 0 0 0.2rem rgba(13,110,253,.25)"
                      : "none",
                    "&:hover": {
                      border: "1px solid #0d6efd",
                    },
                  }),
                  valueContainer: (base) => ({
                    ...base,
                    padding: "0 10px",
                  }),
                  input: (base) => ({
                    ...base,
                    margin: 0,
                    fontSize: "14px",
                  }),
                  placeholder: (base) => ({
                    ...base,
                    fontSize: "14px",
                    color: "#6c757d",
                  }),
                  singleValue: (base) => ({
                    ...base,
                    fontSize: "14px",
                    color: "#212529",
                  }),
                  menu: (base) => ({
                    ...base,
                    zIndex: 9999,
                    borderRadius: "6px",
                  }),
                  menuPortal: (base) => ({
                    ...base,
                    zIndex: 9999,
                  }),
                }}
              />
            </div>

            <div className="mb-3">
              <label className="form-label fw-semibold">Mode</label>

              <select
                className="form-select"
                value={rescheduleMode}
                onChange={(e) => setRescheduleMode(e.target.value)}
              >
                <option value="Online">Online</option>
                <option value="Offline">Offline</option>
              </select>
            </div>

            {rescheduleDate && (() => {
              const campaignAmount = getCampaignAmountForDate(rescheduleDate);
              const campaignTitle = getCampaignTitleForDate(rescheduleDate);
              if (campaignAmount > 0) {
                return (
                  <div className="alert alert-info mt-2">
                    <small>
                      <strong>Campaign Note:</strong> This date has an active campaign "{campaignTitle}" with amount ₹{campaignAmount}
                    </small>
                  </div>
                );
              }
              return null;
            })()}
          </Modal.Body>

          <Modal.Footer>
            <button
              type="button"
              className="btn btn-outline-secondary px-4"
              onClick={() => {
                setShowRescheduleModal(false);
                window.isRescheduling = false;
              }}
              disabled={rescheduleLoading}
            >
              Cancel
            </button>

            <button
              type="button"
              className="btn btn-primary px-4"
              onClick={handleRescheduleWithApi}
              disabled={rescheduleLoading || !rescheduleDate || !rescheduleBranch}
            >
              {rescheduleLoading ? (
                <>
                  <span
                    className="spinner-border spinner-border-sm me-2"
                    role="status"
                  ></span>
                  Rescheduling...
                </>
              ) : (
                "Reschedule"
              )}
            </button>
          </Modal.Footer>
        </Modal>
      )}

      {/* Collect Payment Modal */}
      <Modal
        show={showPaymentModal}
        onHide={() => {
          setShowPaymentModal(false)
          setSelectedAppointmentForPaymentLink(null)
          setLeadEmail("")
          setIsEmailFetched(false)
          setPatientOrLeadName("")
        }}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>{selectedAppointmentForPaymentLink ? "Send Payment Link" : "Collect Payment"}</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          {selectedAppointmentForPaymentLink ? (
            <div>
              <div className="mb-3">
                <h6>Appointment Details:</h6>
                <p>
                  {patientOrLeadName && <strong>Patient: </strong>}
                  {patientOrLeadName || selectedAppointmentForPaymentLink.patient_name || ""}
                </p>
                <p>
                  <strong>Date:</strong> {(() => {
                    const [year, month, day] = selectedAppointmentForPaymentLink.date.split("-")
                    return `${day}-${month}-${year}`
                  })()}
                </p>
                
                <p className="mb-1">
                  <strong>Mode:</strong>{" "}
                  <span style={{
                    background: selectedAppointmentForPaymentLink.mode === "ONLINE" ? "#17a2b8" : "#6c757d",
                    color: "#fff",
                    padding: "2px 8px",
                    borderRadius: "6px",
                    fontSize: "0.8rem",
                    fontWeight: "600",
                  }}>
                    {selectedAppointmentForPaymentLink.mode || "OFFLINE"}
                  </span>
                </p>
                
                {(() => {
                  const campaignAmount = getCampaignAmountForDate(selectedAppointmentForPaymentLink.date);
                  const campaignTitle = getCampaignTitleForDate(selectedAppointmentForPaymentLink.date);
                  
                  if (campaignAmount > 0) {
                    return (
                      <div className="mb-3">
                        <strong>Campaign Amount:</strong>{' '}
                        <span className="fw-bold text-success">₹{campaignAmount}</span>
                        <small className="text-muted d-block">
                          Campaign: {campaignTitle}
                        </small>
                      </div>
                    );
                  } else {
                    return (
                      <div className="mb-3">
                        <strong>Amount:</strong>{' '}
                        <span>₹{getAmountByMode(selectedAppointmentForPaymentLink.mode, false, selectedAppointmentForPaymentLink.date, selectedAppointmentForPaymentLink.appointment_camp_reg)}</span>
                        <small className="text-muted d-block">
                          Based on {selectedAppointmentForPaymentLink.mode} mode
                        </small>
                      </div>
                    );
                  }
                })()}
              </div>

              <div className="mb-3">
                <label className="form-label">
                  <strong>Email Address</strong>
                </label>
                <div className="position-relative">
                <input
  type="email"
  className="form-control"
  value={leadEmail}
  onChange={(e) =>
    setLeadEmail(e.target.value)
  }
  placeholder={
    isLeadEmailLoading
      ? "Loading appointment details..."
      : leadEmail
        ? "Enter email"
        : "Email not present"
  }
  disabled={isLeadEmailLoading}
/>
                  {isLeadEmailLoading && (
                    <div className="position-absolute top-50 end-0 translate-middle-y me-2">
                      <div className="spinner-border spinner-border-sm text-primary" role="status">
                        <span className="visually-hidden">Loading...</span>
                      </div>
                    </div>
                  )}
                </div>
                {isLeadEmailLoading && (
                  <small className="text-muted">
                    <i className="fas fa-spinner fa-spin me-1"></i>
                    Loading appointment details...
                  </small>
                )}
                {!leadEmail && !isLeadEmailLoading && isEmailFetched && (
                  <small className="text-danger">No email found. You can manually add the email above.</small>
                )}
                {leadEmail && leadEmail.trim() !== "" && !isLeadEmailLoading && (
                  <small className="text-success">
                    <i className="fas fa-check me-1"></i>
                    Email found and will be used for payment link
                  </small>
                )}
              </div>

              <div className="alert alert-info">
                <i className="fas fa-info-circle me-2"></i>A payment link will be generated and sent to the patient. The
                patient can complete the payment remotely.
              </div>
            </div>
          ) : (
            <form onSubmit={handleCollectPayment}>
              {(() => {
                let appointmentDate = null;
                let appointmentMode = null;
                let appointmentCampReg = null;
                
                if (paymentDetails.appointmentId) {
                  const targetAppointment = appointments.find(
                    a => a.appointment_id === paymentDetails.appointmentId
                  );
                  if (targetAppointment) {
                    appointmentDate = targetAppointment.date;
                    appointmentMode = targetAppointment.mode;
                    appointmentCampReg = targetAppointment.appointment_camp_reg;
                  }
                }
                
                if (!appointmentDate && selectedAppointment) {
                  appointmentDate = selectedAppointment.date;
                  appointmentMode = selectedAppointment.mode;
                  appointmentCampReg = selectedAppointment.appointment_camp_reg;
                }
                
                const campaignAmount = getCampaignAmountForDate(appointmentDate);
                const campaignTitle = getCampaignTitleForDate(appointmentDate);
                
                if (campaignAmount > 0) {
                  return (
                    <div className="mb-3">
                      <strong>Campaign Details</strong>
                      <div className="mt-2 p-3 bg-light rounded border">
                        <div className="fw-bold text-primary mb-2">
                          <i className="fas fa-tag me-2"></i>
                          {campaignTitle}
                        </div>
                        <div className="row">
                          <div className="col-6">
                            <small className="text-muted">Campaign Amount</small>
                            <div className="fw-bold text-success fs-5">₹{campaignAmount}</div>
                          </div>
                          {appointmentCampReg === "Campaign" && (
                            <div className="col-6">
                              <small className="text-muted">Registration Type</small>
                              <div className="fw-bold text-primary">Campaign Registration</div>
                            </div>
                          )}
                        </div>
                        <small className="text-muted d-block mt-2">
                          <i className="fas fa-info-circle me-1"></i>
                          This appointment is under an active campaign
                        </small>
                      </div>
                    </div>
                  );
                }
                
                if (isServiceCaller) {
                  return (
                    <>
                      <div className="form-group mb-3">
                        <label className="fw-semibold mb-2">Select Fee Type</label>
                        <select
                          className="form-control"
                          value={selectedFeeOption}
                          onChange={(e) => {
                            const selected = e.target.value;
                            setSelectedFeeOption(selected);
                            if (appointmentMode === "Online") {
                              setFeeValue(
                                selected === "Consulting Fees"
                                  ? onlineConsultingFees
                                  : onlineRepeatingConsultingFees
                              );
                            } else {
                              setFeeValue(
                                selected === "Consulting Fees"
                                  ? consultingFees
                                  : repeatingConsultingFees
                              );
                            }
                          }}
                        >
                          <option value="Consulting Fees">Consulting Fees</option>
                          <option value="Repeating Consulting Fees">Repeating Consulting Fees</option>
                        </select>
                      </div>

                      <div className="mb-3">
                        <label className="fw-semibold mb-2">Amount</label>
                        <input
                          type="text"
                          className="form-control bg-light"
                          value={`₹ ${feeValue}`}
                          readOnly
                          disabled
                          style={{ fontWeight: 500, fontSize: "1.1rem" }}
                        />
                      </div>
                    </>
                  );
                }
                
                const displayAmount = appointmentMode === "Offline" 
                  ? (consultingFees || 0) 
                  : (onlineConsultingFees || 0);
                return (
                  <div className="mb-3">
                    <strong>Amount:</strong>{' '}
                    <span className="fw-bold fs-5">₹{Number(displayAmount)}</span>
                    <small className="text-muted d-block">
                      Based on {appointmentMode} mode
                    </small>
                  </div>
                );
              })()}

              <div className="mb-3 mt-4">
                <label className="form-label fw-semibold">
                  Payment Mode <span className="text-danger">*</span>
                </label>
                <Select
                  name="paymentType"
                  options={[
                    { value: "Online", label: "Online" },
                    { value: "Bankingcard", label: "Banking Card" },
                    { value: "Upi", label: "UPI" },
                    { value: "Offline", label: "Cash" },
                  ]}
                  value={
                    paymentDetails.paymentType
                      ? { value: paymentDetails.paymentType, label: paymentDetails.paymentType }
                      : null
                  }
                  onChange={(selectedOption) =>
                    setPaymentDetails((prev) => ({
                      ...prev,
                      paymentType: selectedOption ? selectedOption.value : "",
                    }))
                  }
                  placeholder="Select Payment Mode"
                  styles={{
                    control: (provided) => ({ ...provided, minHeight: "50px", height: "50px" }),
                    singleValue: (provided) => ({ ...provided, lineHeight: "50px" }),
                    dropdownIndicator: (provided) => ({ ...provided, padding: "4px" }),
                    indicatorSeparator: () => ({ display: "none" }),
                    menu: (provided) => ({ ...provided, zIndex: 9999 }),
                  }}
                />
                {submitted && !paymentDetails.paymentType && (
                  <p className="text-danger small mt-1">Payment mode is required</p>
                )}
              </div>

              {paymentDetails.paymentType === "Offline" && (
                <>
                  <div className="mb-3">
                    <label className="form-label">Coupon Code (Optional)</label>
                    <div className="input-group">
                      <input
                        type="text"
                        className="form-control"
                        name="coupon"
                        value={paymentDetails.coupon}
                        onChange={handlePaymentInputChange}
                        placeholder="Enter coupon code"
                        ref={couponInputRef}
                      />
                      <button className="btn btn-outline-secondary" type="button" onClick={handleApplyCoupon}>
                        Apply
                      </button>
                    </div>
                  </div>

                  {paymentDetails.finalAmount !== null && (
                    <div className="mb-3">
                      <strong>Final Amount: ₹{Number(paymentDetails.finalAmount || 0)}</strong>
                    </div>
                  )}
                </>
              )}
            </form>
          )}
        </Modal.Body>

        {/* <Modal.Footer>
          {selectedAppointmentForPaymentLink ? (
            <div className="d-flex gap-2">
              <Button
                variant="secondary"
                style={{ height: "54px" }}
                onClick={() => {
                  setShowPaymentModal(false)
                  setSelectedAppointmentForPaymentLink(null)
                  setLeadEmail("")
                  setIsEmailFetched(false)
                  setPatientOrLeadName("")
                }}
              >
                Close
              </Button>
              {(() => {
                const hasEmail = leadEmail && leadEmail.trim() !== ""
                const isEmailRequired = hasEmail
                const isEmailLoading = isLeadEmailLoading || !isEmailFetched
                const isButtonDisabled = (isEmailRequired && !hasEmail) || isEmailLoading;

                const amountToSend = getAmountByMode(
                  selectedAppointmentForPaymentLink.mode, 
                  false, 
                  selectedAppointmentForPaymentLink.date,
                  selectedAppointmentForPaymentLink.appointment_camp_reg
                );

                const sendButton = (
                  <SendPaymentLinkButton
                    patientId={
                      selectedAppointmentForPaymentLink?.patient_id ||
                      patientId || ""
                    }
                    appointmentId={selectedAppointmentForPaymentLink.appointment_id}
                    leadId={selectedAppointmentForPaymentLink.lead_id || leadId}
                    prescriptionId={null}
                    queueId={null}
                    patientName={patientOrLeadName || selectedAppointmentForPaymentLink.patient_name || "Patient"}
                    amount={amountToSend}
                    paymentFor="Appointment"
                    paymentType="appointment"
                    disabled={isButtonDisabled}
                    email={leadEmail}
                    onSuccess={() => {
                      setShowPaymentModal(false)
                      setSelectedAppointmentForPaymentLink(null)
                      setLeadEmail("")
                      setIsEmailFetched(false)
                      setPatientOrLeadName("")
                    }}
                  />
                )

                if (isEmailRequired || isEmailLoading) {
                  const tooltipText = isEmailLoading
                    ? "Loading email details..."
                    : "Email not found. Please update email in lead/patient details."

                  return (
                    <OverlayTrigger placement="top" overlay={<Tooltip id="email-tooltip">{tooltipText}</Tooltip>}>
                      <span className="d-inline-block">{sendButton}</span>
                    </OverlayTrigger>
                  )
                }

                return sendButton
              })()}
            </div>
          ) : (
            <Button
              variant="primary"
              onClick={handleCollectPayment}
              disabled={loading}
            >
              {loading ? (
                <>
                  <Loader size="sm" />
                  <span className="ms-2">Processing...</span>
                </>
              ) : (
                "Collect Payment"
              )}
            </Button>
          )}
        </Modal.Footer> */}<Modal.Footer>
  {selectedAppointmentForPaymentLink ? (
    <div className="d-flex gap-2">
      {/* Close button with Send button styling */}
      <Button
  variant="danger"
  style={{
    border: "1px solid #dc3545",
    background: "transparent",
    padding: "6px 20px",
    fontSize: "16px",
    fontWeight: "500",
    borderRadius: "6px",
    minHeight: "54px",
    lineHeight: "1.5",
    color: "#dc3545",
  }}
  onClick={() => {
    setShowPaymentModal(false);
    setSelectedAppointmentForPaymentLink(null);
    setLeadEmail("");
    setIsEmailFetched(false);
    setPatientOrLeadName("");
  }}
>
  Close
</Button>

      {(() => {
        const hasEmail = leadEmail && leadEmail.trim() !== "";
        const isEmailRequired = hasEmail;
        const isEmailLoading = isLeadEmailLoading || !isEmailFetched;
        const isButtonDisabled = (isEmailRequired && !hasEmail) || isEmailLoading;

        const amountToSend = getAmountByMode(
          selectedAppointmentForPaymentLink.mode,
          false,
          selectedAppointmentForPaymentLink.date,
          selectedAppointmentForPaymentLink.appointment_camp_reg
        );

        const sendButton = (
          <SendPaymentLinkButton
            patientId={selectedAppointmentForPaymentLink?.patient_id || patientId || ""}
            appointmentId={selectedAppointmentForPaymentLink.appointment_id}
            leadId={selectedAppointmentForPaymentLink.lead_id || leadId}
            prescriptionId={null}
            queueId={null}
            patientName={patientOrLeadName || selectedAppointmentForPaymentLink.patient_name || "Patient"}
            amount={amountToSend}
            paymentFor="Appointment"
            paymentType="appointment"
            disabled={isButtonDisabled}
            email={leadEmail}
            onSuccess={() => {
              setShowPaymentModal(false);
              setSelectedAppointmentForPaymentLink(null);
              setLeadEmail("");
              setIsEmailFetched(false);
              setPatientOrLeadName("");
            }}
          />
        );

        if (isEmailRequired || isEmailLoading) {
          const tooltipText = isEmailLoading
            ? "Loading email details..."
            : "Email not found. Please update email in lead/patient details.";

          return (
            <OverlayTrigger placement="top" overlay={<Tooltip id="email-tooltip">{tooltipText}</Tooltip>}>
              <span className="d-inline-block">{sendButton}</span>
            </OverlayTrigger>
          );
        }

        return sendButton;
      })()}
    </div>
  ) : (
    <Button variant="primary" onClick={handleCollectPayment} disabled={loading}>
      {loading ? (
        <>
          <Loader size="sm" />
          <span className="ms-2">Processing...</span>
        </>
      ) : (
        "Collect Payment"
      )}
    </Button>
  )}
</Modal.Footer>

      </Modal>
    </>
  )
}

CommonAppointmentCalendar.propTypes = {
  module: PropTypes.string,
  leadId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  patientId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  doctorId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  appointmentType: PropTypes.string,
  currentPatientId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  currentLeadId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  allowBooking: PropTypes.bool,
  onBookingSuccess: PropTypes.func,
  onCancelSuccess: PropTypes.func,
  onRescheduleSuccess: PropTypes.func,
  onRescheduleError: PropTypes.func,
  onRescheduleStart: PropTypes.func,
  onRescheduleEnd: PropTypes.func,
  initialStartDate: PropTypes.instanceOf(Date),
  initialEndDate: PropTypes.instanceOf(Date),
  initialAppointmentId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  currentAppointmentData: PropTypes.object,
  isNewBooking: PropTypes.bool,
  onHide: PropTypes.func,
}

export default CommonAppointmentCalendar;